<template>
  <div class="dim">
    <spinner v-if="loading"></spinner>
    <div v-show="!loading">
      <ValidationObserver ref="observer" v-slot="{ handleSubmit, failed } ">
        <div id="payment_form" type="form">
          <div class="row">
            <div class="col-xs-12 col-md-8 col-md-offset-2 col-lg-5 col-lg-offset-2">
              <div v-if="showingDivisions" id="divisions">
                <division-selection-step
                  :wrestlers="selectedWrestlers"
                  :session="session"
                  :wrestler-divisions="wrestlerDivisions"
                  :brand-color="brandColor"
                  @update:wrestler-divisions="handleWrestlerDivisionsUpdate"
                />
              </div>
              <div v-if="showingReview" class="ibox">
                <div id="review-order" class="ibox-content">
                  <h1 class="p-t-m m-t-none font-heavy">Review order</h1>
                  <h2 class="m-t-md text-18 font-semi">Select
                    <span v-if="recurringSession">Subscription</span>
                    <span v-else>
                      Payment
                    </span>
                    Option
                  </h2>
                  <div id="payment-option-picker" class="grid sm-grid-cols-1 gap-y-3">
                    <div v-for="option in activePaymentOptions" @click="selectOption(option)"
                         :key="'payment-option-' + option.id"
                         class="border-solid rounded-xs p-m flex align-items-start gap-x-m"
                         :class="selectedPaymentOptionId === option.id ? selectedPillStyles : pillStyles">
                      <div>
                        <label>
                          <input type="radio" v-model="selectedPaymentOptionId" v-bind:value="option.id"
                                 :style="selectedPaymentOptionId === option.id ? 'accent-color: #199473;' : ''">
                        </label>
                      </div>
                      <div class="w-full h-full flex flex-col">
                        <div class="m-t-none text-semi text-14">
                          {{ option.name }}
                        </div>
                        <div v-html="optionDescription(option)"></div>
                        <div class="m-t-auto rounded-xxs p-y-m p-x-sm flex align-items-center"
                             :class="selectedPaymentOptionId === option.id ? 'bg-green-50' : 'bg-gray-50'">
                          <div class="flex flex-col">
                            <template v-if="isTeamRegistration">
                              <div v-if="option.deposit_amount && option.deposit_amount > 0" class="text-14 font-semi">
                                Deposit: {{ dueNowDisplay(option) }}
                              </div>
                              <div class="text-14" :class="{ 'm-t-xs': option.deposit_amount && option.deposit_amount > 0 }">
                                <template v-if="option.payment_strategy === 'installments'">
                                  <div v-html="installmentListForTeamReg(option)"></div>
                                </template>
                                <template v-else>
                                  After approval: {{ afterApprovalTotal(option) }}
                                </template>
                              </div>
                            </template>
                            <template v-else>
                              <div class="text-14 font-semi">
                                Due now: {{ dueNowDisplay(option) }}
                              </div>
                              <div class="m-t-xs" v-if="option.payment_strategy === 'installments'"
                                   v-html="futureInstallmentListFor(option)">
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-for="plan in activeBillingPlans" @click="selectedBillingPlanId = plan.id"
                         :key="'active-plan-' + plan.id"
                         class="border-solid rounded-xs p-m flex align-items-start gap-x-m"
                         :class="selectedBillingPlanId === plan.id ? selectedPillStyles : pillStyles">
                      <div>
                        <label>
                          <input type="radio" v-model="selectedBillingPlanId" v-bind:value="plan.id"
                                 :style="selectedBillingPlanId === plan.id ? 'accent-color: #199473;' : ''">
                        </label>
                      </div>
                      <div class="w-full h-full flex flex-col">
                        <div class="m-t-none text-semi text-14">
                          {{ plan.name }}
                        </div>
                        <div v-if="classPassLabel(plan)" class="text-12 inline-flex align-items-center gap-x-xxs m-t-xxs">
                          <i class="fa-solid fa-ticket"></i> {{ classPassLabel(plan) }}
                        </div>
                        <div v-html="planDescription(plan)"></div>
                        <div class="m-t-auto rounded-xxs p-y-m p-x-sm flex align-items-center"
                             :class="selectedBillingPlanId === plan.id ? 'bg-green-50' : 'bg-gray-50'">
                          <div class="flex flex-col">
                            <div class="text-14 font-semi">
                              {{ formatAsCurrency(plan.amount) }} {{ planPriceExtra(plan) }}
                            </div>
                            <div v-if="formatBillingStartDate(plan.billing_starts_at)" class="text-12 m-t-xxs">
                              Billing starts {{ formatBillingStartDate(plan.billing_starts_at) }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="m-t-lg">
                    <h2 id="your-selection" class="text-18 font-semi">Your Selection</h2>
                    <div v-if="chosenPlanOrPaymentOption">
                      <spinner v-if="loadingPreview"></spinner>
                      <div v-if="!loadingPreview">
                        <div class="panel panel-default m-t-sm">
                          <div class="panel-body">
                            <h3 class="font-semi m-b-xxs">{{ session.name }}</h3>
                            <div
                                class="m-b-md"
                            >
                              {{ chosenPlanOrPaymentOption.name }}
                            </div>
                            <hr/>
                            <!-- for subscriptions -->
                            <template v-if="selectedBillingPlan">
                              <div v-if="classPassLabel(selectedBillingPlan)" class="text-12 inline-flex align-items-center gap-x-xxs m-b-sm">
                                <i class="fa-solid fa-ticket"></i> {{ classPassLabel(selectedBillingPlan) }}
                              </div>
                              <div class="m-b-md font-small" v-html="planDescription(selectedBillingPlan)">
                              </div>
                              <div v-if="deferredBillingDate" class="alert bg-blue-50 text-14 m-b-md">
                                <i class="fa-solid fa-circle-info m-r-xxs"></i>
                                <template v-if="deferredHasOneTimeFee">
                                  <span class="font-semi">Only the one-time registration fee is due today.</span> Recurring billing of {{ formatAsCurrency(selectedBillingPlan.amount) }} {{ planPriceExtra(selectedBillingPlan) }} starts on {{ deferredBillingDate }}.
                                </template>
                                <template v-else>
                                  <span class="font-semi">You won't be charged today.</span> Billing starts on {{ deferredBillingDate }} at {{ formatAsCurrency(selectedBillingPlan.amount) }} {{ planPriceExtra(selectedBillingPlan) }}.
                                </template>
                              </div>
                              <div class="font-small -m-t-sm m-b-md" v-if="selectedWrestlers.length > 1">You are signing
                                up for
                                <span class="font-semi">{{ selectedWrestlers.length }} recurring charges</span>, one for
                                each wrestler.
                              </div>
                              <div v-if="previewRequest && !loadingPreview" v-for="subRequest in subscriptionsRequested">
                                <hr/>
                                <h3 class="m-b-none font-semi m-b-sm text-16">
                                  {{ subRequest.wrestler_profile.first_name }}
                                  {{ subRequest.wrestler_profile.last_name }}
                                </h3>
                                <div v-for="plan in subRequest.plan_requests"
                                     class="flex justify-space-between align-items-baseline">
                                  <p class="m-b-none">{{ plan.billing_plan.name }}</p>
                                  <p class="m-b-none flex-shrink-0">
                                    {{ formatAsCurrency(plan.billing_plan.amount) }}
                                  </p>
                                </div>
                                <div v-if="subRequest.coupon != null"
                                     class="flex justify-space-between align-items-baseline m-t-xs">
                                  <div class="m-b-none flex align-items-center flex-wrap gap-x-xs">
                                    <p class="m-b-none">
                                      Scholarship
                                    </p>
                                    <div
                                        class="text-sm bg-gray-50 text-gray-600 rounded-xxs flex gap-x-xs align-items-center"
                                        style="padding: 2px 4px;">
                                      <div>
                                        {{ subRequest.coupon.code }}
                                      </div>
                                      <i class="fa-solid fa-xmark text-gray-300 cursor-pointer"
                                         @click="removeCoupon(subRequest)"></i>
                                    </div>
                                  </div>
                                  <p class="m-b-none flex-shrink-0">
                                    - {{ formatAsCurrency(subRequest.discount_total) }}
                                  </p>
                                </div>
                                <div class="m-t-sm flex justify-space-between align-items-baseline text-heavy">
                                  <p class="m-b-none font-semi text-heavy text-14">Recurring total for
                                    {{ subRequest.wrestler_profile.first_name }}</p>
                                  <p class="m-b-none flex-shrink-0 text-14">
                                    {{ formatAsCurrency(subRequest.total_per_interval) }} {{
                                      planPriceExtra(subRequest)
                                    }}
                                  </p>
                                </div>
                                <div v-if="deferredBillingDate && subRequest.one_time_fee_total > 0">
                                  <hr/>
                                  <div class="m-t-m flex justify-space-between align-items-baseline">
                                    <p class="m-b-none">One-time Registration Fee</p>
                                    <p class="m-b-none flex-shrink-0">
                                      {{ formatAsCurrency(subRequest.one_time_fee_total) }}
                                    </p>
                                  </div>
                                  <hr/>
                                  <div class="m-t-sm flex justify-space-between align-items-baseline text-heavy">
                                    <p class="m-b-none font-semi text-heavy text-14">
                                      Due today for {{ subRequest.wrestler_profile.first_name }}
                                    </p>
                                    <p class="m-b-none flex-shrink-0 text-14">
                                      {{ formatAsCurrency(subRequest.one_time_fee_total) }}
                                    </p>
                                  </div>
                                </div>
                                <div v-else-if="deferredBillingDate">
                                  <hr/>
                                  <div class="m-t-sm flex justify-space-between align-items-baseline text-heavy">
                                    <p class="m-b-none font-semi text-heavy text-14">
                                      Due today for {{ subRequest.wrestler_profile.first_name }}
                                    </p>
                                    <p class="m-b-none flex-shrink-0 text-14">
                                      {{ formatAsCurrency(0) }}
                                    </p>
                                  </div>
                                </div>
                                <div v-else-if="subRequest.one_time_fee_total > 0">
                                  <hr/>
                                  <div class="m-t-m flex justify-space-between align-items-baseline">
                                    <p class="m-b-none">One-time Registration Fee</p>
                                    <p class="m-b-none flex-shrink-0">
                                      {{ formatAsCurrency(subRequest.one_time_fee_total) }}
                                    </p>
                                  </div>
                                  <hr/>
                                  <div v-if="anchorDayOfMonth">
                                    <div class="m-t-sm flex justify-space-between align-items-baseline">
                                      <p class="m-b-none text-14">
                                        Prorated first charge for {{ subRequest.wrestler_profile.first_name }}
                                      </p>
                                      <div v-if="subRequest.total_due_today !== subRequest.total_per_interval">
                                        <p class="m-b-none text-14">
                                          {{ formatAsCurrency(subRequest.total_due_today) }}
                                        </p>
                                      </div>
                                      <p v-else class="m-b-none flex-shrink-0 text-14">
                                        {{ formatAsCurrency(subRequest.total_per_interval) }}
                                      </p>
                                    </div>
                                    <div class="alert bg-green-50 text-14 font-semi m-t-xs">
                                      <i class="fa-solid fa-circle-info m-r-xxs"></i> Bills on the {{ anchorDayOfMonth }} each month. Today's charge covers the prorated period until your first full payment on the {{ anchorDayOfMonth }}.
                                    </div>
                                    <hr/>
                                  </div>
                                  <div class="m-t-sm flex justify-space-between align-items-baseline text-heavy">
                                    <p class="m-b-none font-semi text-heavy text-14">
                                      Total due now for {{ subRequest.wrestler_profile.first_name }}
                                    </p>
                                    <p v-if="subRequest.total_due_today !== subRequest.total_per_interval" class="m-b-none flex-shrink-0 text-14">
                                      {{ formatAsCurrency(subRequest.total_due_today + subRequest.one_time_fee_total) }}
                                    </p>
                                    <p v-else class="m-b-none flex-shrink-0 text-14">
                                      {{ formatAsCurrency(subRequest.total_per_interval + subRequest.one_time_fee_total) }}
                                    </p>
                                  </div>
                                </div>
                                <div v-else-if="anchorDayOfMonth">
                                  <hr/>
                                  <div class="m-t-sm flex justify-space-between align-items-baseline text-heavy">
                                    <p class="m-b-none font-semi text-heavy text-14">
                                      Prorated total for {{ subRequest.wrestler_profile.first_name }}
                                    </p>
                                    <div v-if="subRequest.total_due_today !== subRequest.total_per_interval">
                                      <p class="m-b-none flex-shrink-0 text-14">
                                        {{ formatAsCurrency(subRequest.total_due_today) }}
                                      </p>
                                    </div>
                                    <p v-else class="m-b-none flex-shrink-0 text-14">
                                      {{ formatAsCurrency(subRequest.total_per_interval) }}
                                    </p>
                                  </div>
                                  <div class="alert bg-green-50 text-14 font-semi m-t-xs">
                                    <i class="fa-solid fa-circle-info m-r-xxs"></i> Bills on the {{ anchorDayOfMonth }} each month. Today's charge covers the prorated period until your first full payment on the {{ anchorDayOfMonth }}.
                                  </div>
                                  <hr/>
                                </div>
                                <div class="m-t-m">
                                  <div role="form" v-if="subRequest.coupon == null">
                                    <div class="form-group">
                                      <label class="font-normal text-12">Add scholarship code for
                                        {{ subRequest.wrestler_profile.first_name }}</label>
                                      <div class="flex justify-space-between gap-x-xxs">
                                        <input
                                            v-model="wrestlerCoupons[subRequest.wrestler_profile.id].coupon.code"
                                            type="text"
                                            class="form-control font-no-ios-zoom"
                                            placeholder="Enter code (case sensitive)"
                                        />
                                        <ladda-button
                                            @lbClicked="applyCoupon(subRequest)"
                                            el-class="btn-primary flex-shrink-0"
                                            :el-identifier="`apply-coupon-button-${subRequest.wrestler_profile.id}`"
                                            :loading="loadingPreview"
                                        >
                                          Apply
                                        </ladda-button>
                                      </div>
                                      <p class="text-12 text-red-500 leading-5 m-l-xxs m-t-xs"
                                         v-if="wrestlerCoupons[subRequest.wrestler_profile.id].previously_tried_coupon_code !== '' && subRequest.coupon == null">
                                        {{
                                          wrestlerCoupons[subRequest.wrestler_profile.id].previously_tried_coupon_code
                                        }} not applied. Check your capitalization and spelling
                                        or contact your coach.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </template>
                            <!-- for non-recurring: team registration per-wrestler display -->
                            <template v-if="!recurringSession && isTeamRegistration">
                              <div v-for="(checkout, cIndex) in checkouts"
                                   :key="'checkout-' + getCheckoutWrestlerId(checkout)"
                              >
                                <hr v-if="cIndex > 0"/>
                                <h3 class="m-b-none font-semi m-b-sm text-16">
                                  {{ getCheckoutWrestlerName(checkout) }}
                                </h3>
                                <template v-for="invoice in checkout.manual_invoices">
                                  <template v-for="(item, itemIndex) in invoice.invoice_items">
                                    <div :class="itemIndex === invoice.invoice_items.length - 1 ? 'row m-b-xs' : 'row'">
                                      <div class="col-xs-8">
                                        <p class="m-b-none">{{ item.description }}</p>
                                      </div>
                                      <div class="col-xs-4 text-right">
                                        <p class="m-b-none">{{ formatAsCurrency(item.amount) }}</p>
                                      </div>
                                    </div>
                                  </template>
                                </template>
                                <div class="m-t-m">
                                  <div v-if="getCheckoutCoupon(checkout) != null"
                                       class="flex justify-space-between align-items-baseline m-t-xs">
                                    <div class="m-b-none flex align-items-center flex-wrap gap-x-xs">
                                      <p class="m-b-none">Scholarship</p>
                                      <div class="text-sm bg-gray-50 text-gray-600 rounded-xxs flex gap-x-xs align-items-center"
                                           style="padding: 2px 4px;">
                                        <div>{{ getCheckoutCoupon(checkout).code }}</div>
                                        <i class="fa-solid fa-xmark text-gray-300 cursor-pointer"
                                           @click="removeTeamRegCoupon(getCheckoutWrestlerId(checkout))"></i>
                                      </div>
                                    </div>
                                  </div>
                                  <div role="form" v-else>
                                    <div class="form-group">
                                      <label class="font-normal text-12">Add scholarship code for
                                        {{ getCheckoutWrestlerName(checkout) }}</label>
                                      <div class="flex justify-space-between gap-x-xxs">
                                        <input
                                            v-model="wrestlerCoupons[getCheckoutWrestlerId(checkout)].coupon.code"
                                            type="text"
                                            class="form-control font-no-ios-zoom"
                                            placeholder="Enter code (case sensitive)"
                                        />
                                        <ladda-button
                                            @lbClicked="applyTeamRegCoupon(getCheckoutWrestlerId(checkout))"
                                            el-class="btn-primary flex-shrink-0"
                                            :el-identifier="`apply-coupon-button-${getCheckoutWrestlerId(checkout)}`"
                                            :loading="loadingPreview"
                                        >
                                          Apply
                                        </ladda-button>
                                      </div>
                                      <p class="text-12 text-red-500 leading-5 m-l-xxs m-t-xs"
                                         v-if="wrestlerCoupons[getCheckoutWrestlerId(checkout)].previously_tried_coupon_code !== '' && getCheckoutCoupon(checkout) == null">
                                        {{ wrestlerCoupons[getCheckoutWrestlerId(checkout)].previously_tried_coupon_code }}
                                        not applied. Check your capitalization and spelling or contact your coach.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <hr/>
                              <div class="row">
                                <div class="col-xs-8">
                                  <span :class="hasMultiplePayments ? '' : 'text-heavy'">Total</span>
                                </div>
                                <div class="col-xs-4 text-right">
                                  <span :class="hasMultiplePayments ? '' : 'text-heavy'">{{ total(acceptedInsuranceTotal) }}</span>
                                </div>
                              </div>
                              <div class="row text-heavy" v-if="hasMultiplePayments">
                                <div class="col-xs-8">Due now</div>
                                <div class="col-xs-4 text-right">
                                  {{ installmentTotalDueNow(acceptedInsuranceTotal) }}
                                </div>
                              </div>
                              <div v-if="selectedDepositAmount > 0" class="m-t-sm text-heavy alert alert-info">
                                <i class="fa-solid fa-circle-info m-r-xxs"></i>
                                Your deposit will be collected now. The remaining balance will be charged if your application is approved.
                              </div>
                            </template>
                            <!-- for non-recurring: non-team flat display -->
                            <template v-if="!recurringSession && !isTeamRegistration">
                              <template v-for="(invoice, index) in invoices">
                                <template v-if="invoices.length > 1">
                                  <div class="row m-b-xs">
                                    <div class="col-xs-12">
                                      <p class="m-b-none">
                          <span class="text-semi"
                          >Invoice #{{ index + 1 }}&nbsp;</span
                          >
                                        <small>{{ due(invoice) }}</small>
                                      </p>
                                    </div>
                                  </div>
                                </template>
                                <template v-for="(item, index) in invoice.invoice_items">
                                  <div
                                      :class="
                        index === invoice.invoice_items.length - 1
                          ? 'row m-b-lg'
                          : 'row'
                      "
                                  >
                                    <div class="col-xs-8">
                                      <p class="m-b-none">
                                        {{ item.description }}
                                      </p>
                                    </div>
                                    <div class="col-xs-4 text-right">
                                      <p class="m-b-none">
                                        {{ formatAsCurrency(item.amount) }}
                                      </p>
                                    </div>
                                  </div>
                                </template>
                              </template>
                              <hr/>
                              <div>
                                <div
                                    role="form"
                                    v-if="showCouponForm && !validCouponApplied"
                                >
                                  <div class="form-group">
                                    <label class="font-normal text-12">Add scholarship code</label>
                                    <div class="flex justify-space-between gap-x-xxs">
                                      <input
                                          v-model="couponCode"
                                          type="text"
                                          class="form-control font-no-ios-zoom"
                                          placeholder="Enter code (case sensitive)"
                                      />
                                      <ladda-button
                                          @lbClicked="attemptCoupon"
                                          el-class="btn-primary flex-shrink-0"
                                          el-identifier="apply-coupon-button"
                                          :loading="loadingPreview"
                                      >
                                        Apply
                                      </ladda-button>
                                    </div>
                                    <p class="text-12 text-red-500 leading-5 m-l-xxs m-t-xs"
                                       v-if="!loadingPreview && previouslyTriedCouponCode !== '' && !validCouponApplied">
                                      {{ previouslyTriedCouponCode }} not applied. Check your capitalization and
                                      spelling
                                      or contact your coach.
                                    </p>
                                  </div>
                                </div>
                                <p class="m-t-xxs">
                                  <small v-if="validCouponApplied">
                                    <span class="text-success text-semi text-12">{{ couponCode }} applied!</span>
                                    <a class="text-danger" @click.prevent="removeCoupon(null)"
                                    >(remove)</a
                                    >
                                  </small><br v-if="validCouponApplied">
                                </p>
                              </div>
                              <hr/>
                              <div class="row">
                                <div class="col-xs-8">
                            <span :class="hasMultiplePayments ? '' : 'text-heavy'">
                              Total
                            </span>
                                </div>
                                <div class="col-xs-4 text-right">
                            <span :class="hasMultiplePayments ? '' : 'text-heavy'">
                              {{ total(acceptedInsuranceTotal) }}
                            </span>
                                </div>
                              </div>
                              <div class="row text-heavy" v-if="hasMultiplePayments">
                                <div class="col-xs-8">Due now</div>
                                <div class="col-xs-4 text-right">
                                  {{ installmentTotalDueNow(acceptedInsuranceTotal) }}
                                </div>
                              </div>
                              <div v-if="selectedDepositAmount > 0" class="m-t-sm text-12 text-gray-500">
                                <i class="fa-solid fa-circle-info m-r-xxs"></i>
                                Your deposit will be collected now. The remaining balance will be charged after your registration is approved.
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else>
                      <p>Please make a selection above to continue.</p>
                    </div>
                  </div>
                  <div id="billing-info" class="m-t-lg" v-if="chosenPlanOrPaymentOption">
                    <h2 class="text-18 font-semi">Billing Info</h2>
                    <div v-if="hasGuessBillingInfo" class="bg bg-gray-50 border-gray-200 rounded-sm flex gap-x-sm align-items-center p-sm m-b-sm">
                      <i class="fa-solid fa-wand-magic-sparkles"></i>
                      <p class="m-y-none">
                        We've prefilled your billing address based on your previous answers. Please review and update as needed.
                        <a @click.prevent="clearBillingInfo" class="text-underline text-gray-600">Tap here to clear billing info fields.</a>
                      </p>
                    </div>
                    <div>
                      <ValidationProvider name="name on card" rules="required" v-slot="{ errors }">
                        <div class="form-group">
                          <label for="name_on_card" class="font-normal">Billing name</label>
                          <input type="text"
                                 autocomplete="cc-name"
                                 name="name on card"
                                 id="name_on_card"
                                 v-model="nameOnCard"
                                 data-cy="name_on_card"
                                 :class="inputClasses(errors[0])">
                          <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                            {{ errors[0] }}
                          </p>
                        </div>
                      </ValidationProvider>
                      <ValidationProvider name="address line 1" rules="required" v-slot="{ errors }">
                        <div class="form-group">
                          <label for="location-input" class="font-normal">
                            Address line 1
                          </label>
                          <input autocomplete="address-line1" v-model="addressLine1" id="location-input" type="text"
                                 data-cy="address_1"
                                 :class="inputClasses(errors[0])">
                          <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                            {{ errors[0] }}
                          </p>
                        </div>
                      </ValidationProvider>
                      <div class="form-group">
                        <label for="address_2" class="font-normal">
                          Address line 2 (optional)
                        </label>
                        <input autocomplete="address-line2" v-model="addressLine2" id="address_2" type="text"
                               class="form-control font-no-ios-zoom">
                      </div>
                      <ValidationProvider name="city" rules="required" v-slot="{ errors }">
                        <div class="form-group">
                          <label for="locality-input" class="font-normal">
                            City
                          </label>
                          <input autocomplete="address-level2" v-model="city" id="locality-input" type="text" ref="city"
                                 :class="inputClasses(errors[0])" data-cy="city">
                          <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                            {{ errors[0] }}
                          </p>
                        </div>
                      </ValidationProvider>
                      <div class="form-group">
                        <div class="row">
                          <div class="col-xs-12 col-sm-6 m-b-only-xs-m">
                            <ValidationProvider :name="isUSA ? 'state' : 'region'" rules="required" v-slot="{ errors }">
                              <label :for="isUSA ? 'administrative_area_level_1-input' : 'region-input'" class="font-normal">
                                {{ isUSA ? 'State' : 'Region/Province' }}
                              </label>
                              <the-mask
                                  v-if="isUSA"
                                  v-model="state"
                                  mask="AA"
                                  type="text"
                                  :class="inputClasses(errors[0])"
                                  ref="state"
                                  autocomplete="address-level1"
                                  id="administrative_area_level_1-input"
                                  data-cy="state"
                              ></the-mask>
                              <input
                                  v-else
                                  v-model="state"
                                  type="text"
                                  :class="inputClasses(errors[0])"
                                  ref="state"
                                  autocomplete="address-level1"
                                  id="region-input"
                                  data-cy="state"
                              >
                              <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                                {{ errors[0] }}
                              </p>
                            </ValidationProvider>
                          </div>
                          <div class="col-xs-12 col-sm-6 m-b-only-xs-m">
                            <ValidationProvider :name="isUSA ? 'zip' : 'postal code'" rules="required" v-slot="{ errors }">
                              <label for="postal_code-input" class="font-normal">
                                {{ isUSA ? 'Zip' : 'Postal Code' }}
                              </label>
                              <the-mask
                                  v-if="isUSA"
                                  v-model="zip"
                                  mask="#####"
                                  type="text"
                                  :class="inputClasses(errors[0])"
                                  ref="zip"
                                  autocomplete="postal-code"
                                  id="postal_code-input"
                                  data-cy="zip"
                              ></the-mask>
                              <input
                                  v-else
                                  v-model="zip"
                                  type="text"
                                  :class="inputClasses(errors[0])"
                                  ref="zip"
                                  autocomplete="postal-code"
                                  id="postal_code-input"
                                  data-cy="zip"
                              >
                              <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                                {{ errors[0] }}
                              </p>
                            </ValidationProvider>
                          </div>
                        </div>
                      </div>
                      <ValidationProvider name="country" rules="required" v-slot="{ errors }">
                        <div class="form-group">
                          <label for="country-input" class="font-normal">
                            Country
                          </label>
                          <select v-model="country" class="form-control font-no-ios-zoom" id="country-input">
                            <option v-for="country in countries" :key="country.short_name" :value="country.short_name">{{ country.long_name }}</option>
                          </select>
                          <p class="m-t-xxs font-small text-red-600" v-if="errors[0]">
                            {{ errors[0] }}
                          </p>
                        </div>
                      </ValidationProvider>
                      <div class="form-group">
                        <label class="checkbox-inline">
                          <input type="checkbox" id="save_billing_info" data-cy="save_billing_info" v-model="reusePaymentMethodInFuture"> Save
                          billing & card info for fast future checkout
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div id="upsells" v-else-if="showingUpsells">
                <div v-if="showDonation && !recurringSession" class="ibox">
                  <div class="ibox-content relative">
                    <div
                        class="absolute w-full bg-green-500 rounded-t-xxs"
                        style="top: 0; left: 0;"
                        :style="{ backgroundColor: brandColor }"
                    >
                      <div class="upper-label text-14 text-heavy p-y-sm p-l-md" :style="{ color: brandTextColor }">We
                        need your support
                      </div>
                    </div>
                    <div class="p-t-xl">
                      <h1 class="m-t-md">Donate to {{ teamName }}</h1>
                      <template v-if="isWrestleLikeAGirl">
                        <p class="m-t-m">
                          Wrestle Like A Girl's mission is to empower girls and women
                          using the sport of wrestling to become leaders in life.
                        </p>
                        <p>
                          Your donations help to continue this mission and grow the
                          sport for all through state sanctioning efforts, empowerment
                          camps and clinics, collegiate growth and development,
                          amplifying and uniting the voice of the wrestling community.
                        </p>
                        <p class="m-b-lg">
                          Without your continuous contribution and support, none of
                          this is possible. We greatly appreciate you. Thank you!
                        </p>
                      </template>
                      <template v-else>
                        <p class="m-t-m">
                          It is our goal to give every child in our community the
                          chance to participate.
                        </p>
                        <p>
                          Your donations help to pay for facilities, insurance,
                          equipment, travel, and give the chance for underpriviledged
                          athletes to compete. If you can spare a few extra dollars,
                          consider adding a donation to your registration.
                        </p>
                        <p class="m-b-lg" style="font-style: italic;">
                          The following examples are for illustrative purposes only. In accordance with IRS guidelines, {{ teamName }} maintains discretion and sole control over
                          the use of all donated funds.
                        </p>
                      </template>
                      <ValidationProvider rules="required" v-slot="{ errors }">
                        <div>
                          <div v-for="tier in donationTiers"
                               @click="donationAmount = tier.amount"
                               class="card-bordered donation-card m-y-m flex gap-x-sm"
                               :class="donationAmount === tier.amount ? selectedPillStyles : pillStyles"
                               :style="donationAmount === tier.amount ? 'accent-color: #199473;' : ''"
                          >
                            <div>
                              <input type="radio" :value="tier.amount" v-model="donationAmount">
                            </div>
                            <div class="flex flex-col">
                              <div class="text-semi text-16">
                                {{ formatAsCurrency(tier.amount) }}
                                <span v-if="isTeamRegistration && selectedDepositAmount > 0">added to deposit</span>
                                <span v-else-if="installmentsPicked">added to first installment</span>
                              </div>
                              <p class="text-gray-600 m-b-xxs">
                                {{ tier.example }}
                              </p>
                            </div>
                          </div>
                          <div
                              @click="donationAmount = 0"
                              class="card-bordered donation-card donation-decline-card m-y-m"
                              :class="donationAmount === 0 ? 'border-1 border-gray-600' : pillStyles"
                              :style="donationAmount === 0 ? 'accent-color: #48647F;' : ''"
                          >
                            <input type="radio" value="0" v-model="donationAmount"><span class="text-16 m-l-sm">No thanks, I do not want to donate</span>
                          </div>
                        </div>
                        <p class="m-t-xxs text-red-600" v-if="errors[0]">
                          Please select a donation amount or decline to continue.
                        </p>
                      </ValidationProvider>
                    </div>
                  </div>
                </div>
                <div v-if="hasCustomUpsellProduct" class="ibox">
                    <div class="ibox-content relative">
                      <div
                          class="absolute w-full bg-gray-200 rounded-t-xxs"
                          style="top: 0; left: 0;"
                          :style="{ backgroundColor: brandColor }"
                      >
                        <div class="upper-label text-14 text-heavy p-y-sm p-l-md text-gray-800" :style="{ color: brandTextColor }">
                          {{ upsellRequired ? 'Required add-on' : 'Add to your order' }}
                        </div>
                      </div>
                      <div class="p-t-xl">
                        <h1 class="m-t-md">{{ customUpsellProduct.name }}</h1>
                        <div v-if="upsellRequired" class="alert alert-warning">
                          <i class="fa-solid fa-circle-info m-r-xxs"></i><strong>This add-on is required to complete registration.</strong> Choose options for each wrestler below.
                        </div>
                        <p class="m-t-sm text-gray-600" v-else>
                          Choose options for each wrestler below
                        </p>
                        <div class="mx-auto mt-12">
                          <div class="grid grid-cols-1 sm-grid-cols-1 gap-x-sm m-t-sm">
                            <!-- Image gallery -->
                            <div class="grid-cols-1">
                              <h2 class="sr-only">Images</h2>
                              <div class="aspect-h-1 aspect-w-1 w-full">
                                <img :src="activeUpsellPhoto.url" alt="Product photo"
                                     class="h-full w-full object-cover object-center rounded-sm">
                              </div>
                              <!-- Image selector -->
                              <div class="mx-auto m-t-sm w-full" v-if="upsellPhotos.length > 1">
                                <div class="grid sm-grid-cols-2 grid-cols-4 gap-6" aria-orientation="horizontal" role="tablist">
                                  <button v-for="(photo, photoIdx) in upsellPhotos"
                                          :key="photoIdx"
                                          class="relative flex cursor-pointer align-items-center justify-center rounded-sm bg-white"
                                          style="height: 12rem;"
                                          type="button"
                                          role="tab"
                                          @click="activeUpsellPhoto = photo">
                                    <span class="absolute inset-0 overflow-hidden rounded-sm">
                                      <img :src="photo.url" alt="Product photo" class="h-full w-full object-cover object-center">
                                    </span>
                                    <span class="pointer-events-none absolute inset-0 rounded-sm border-2 border-solid"
                                          :class="[photo === activeUpsellPhoto ? 'border-blue-500' : 'border-gray-200']"
                                          aria-hidden="true"></span>
                                  </button>
                                </div>
                              </div>
                              <div class="m-t-m" v-html="customUpsellProduct.description"></div>
                            </div>
                            <div class="grid-cols-1 m-t-m">
                              <!-- Loop through each wrestler -->
                              <div v-for="wrestler in selectedWrestlers" :key="wrestler.id" class="m-b-xl border border-solid border-gray-200 rounded-xs p-md">
                                <h3 class="m-t-none text-18 font-semi flex align-items-center gap-x-sm">
                                  <wrestler-icon :wrestler-profile="wrestler" :size="32"></wrestler-icon>
                                  {{ wrestler.first_name }} {{ wrestler.last_name }}
                                </h3>
                                <ValidationProvider rules="required" v-slot="{ errors }">
                                  <div class="mt-8">
                                    <form>
                                      <!-- Price Tier picker (for products with multiple price points but no size/color) -->
                                      <div class="mt-8" v-if="upsellPriceTiers.length > 1 && wrestlerUpsells[wrestler.id]">
                                        <div class="flex items-center justify-between">
                                          <h3 :id="`price-group-label-${wrestler.id}`" class="text-12">Options</h3>
                                        </div>
                                        <div class="mt-2 grid grid-cols-1 gap-3"
                                             role="radiogroup"
                                             :aria-labelledby="`price-group-label-${wrestler.id}`">
                                          <div v-for="(tier,index) in upsellPriceTiers"
                                               :key="index"
                                               role="radio"
                                               :aria-checked="tier.id === wrestlerUpsells[wrestler.id].activePriceTierId"
                                               tabindex="0"
                                               class="upsell-price-button flex flex-col items-center justify-center rounded-sm border border-solid p-sm text-sm cursor-pointer"
                                               :class="[tier.id === wrestlerUpsells[wrestler.id].activePriceTierId ? 'border-transparent' : 'border-gray-200']"
                                               :style="[tier.id === wrestlerUpsells[wrestler.id].activePriceTierId ? selectedBtnStyles : btnStyles]"
                                               @click="setWrestlerUpsellPriceTier(wrestler.id, tier.id)"
                                               @keydown.space.prevent="setWrestlerUpsellPriceTier(wrestler.id, tier.id)"
                                               @keydown.enter.prevent="setWrestlerUpsellPriceTier(wrestler.id, tier.id)"
                                          >
                                            <span class="font-semi">{{ formatAsCurrency(tier.amount, 2) }}</span>
                                            <span class="font-normal text-xs m-t-xxs" v-if="tier.name">{{ tier.name }}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <!-- Colors picker -->
                                      <div class="" v-if="upsellColors.length > 0 && wrestlerUpsells[wrestler.id]">
                                        <h3 :id="`color-group-label-${wrestler.id}`" class="text-12">Color</h3>
                                        <div class="mt-2 flex flex-wrap gap-x-sm gap-y-sm"
                                             role="radiogroup"
                                             :aria-labelledby="`color-group-label-${wrestler.id}`">
                                          <div v-for="(color,index) in upsellColors"
                                               :key="index"
                                               role="radio"
                                               :aria-checked="color === wrestlerUpsells[wrestler.id].activeColor"
                                               tabindex="0"
                                               class="upsell-color-button flex align-items-center justify-center rounded-sm border border-solid p-sm text-sm uppercase cursor-pointer"
                                               :class="[color === wrestlerUpsells[wrestler.id].activeColor ? 'border-transparent' : 'border-gray-200']"
                                               :style="[color === wrestlerUpsells[wrestler.id].activeColor ? selectedBtnStyles : btnStyles]"
                                               @click="setWrestlerUpsellColor(wrestler.id, color)"
                                               @keydown.space.prevent="setWrestlerUpsellColor(wrestler.id, color)"
                                               @keydown.enter.prevent="setWrestlerUpsellColor(wrestler.id, color)"
                                          >
                                            <span class="font-normal">{{ color }}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <!-- Size picker -->
                                      <div class="mt-8" v-if="upsellSizes.length > 0 && wrestlerUpsells[wrestler.id]">
                                        <div class="flex items-center justify-between">
                                          <h3 :id="`size-group-label-${wrestler.id}`" class="text-12">Size</h3>
                                        </div>
                                        <div class="mt-2 grid grid-cols-4 gap-3"
                                             role="radiogroup"
                                             :aria-labelledby="`size-group-label-${wrestler.id}`">
                                          <div v-for="(size,index) in upsellSizes"
                                               :key="index"
                                               role="radio"
                                               :aria-checked="size === wrestlerUpsells[wrestler.id].activeSize"
                                               tabindex="0"
                                               class="upsell-size-button flex items-center justify-center rounded-sm border border-solid p-sm text-sm font-semi uppercase cursor-pointer"
                                               :class="[size === wrestlerUpsells[wrestler.id].activeSize ? 'border-transparent' : 'border-gray-200']"
                                               :style="[size === wrestlerUpsells[wrestler.id].activeSize ? selectedBtnStyles : btnStyles]"
                                               @click="setWrestlerUpsellSize(wrestler.id, size)"
                                               @keydown.space.prevent="setWrestlerUpsellSize(wrestler.id, size)"
                                               @keydown.enter.prevent="setWrestlerUpsellSize(wrestler.id, size)"
                                          >
                                            <span class="font-normal">{{ size }}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <p class="font-small m-t-m text-red-600" v-if="getWrestlerUpsellPrice(wrestler.id) === ''">Please select {{ missingSelectionText }} add to order.</p>
                                      <div class="card-bordered upsell-card donation-card m-y-m flex gap-x-sm"
                                           :class="wrestlerUpsells[wrestler.id].addedToCart ? selectedPillStyles : pillStyles"
                                           @click.prevent="addWrestlerUpsellToCart(wrestler.id)">
                                        <div>
                                          <input type="radio" :name="`upsell-decision-${wrestler.id}`" :value="true" v-model="wrestlerUpsells[wrestler.id].addedToCart">
                                        </div>
                                        <div class="flex flex-col">
                                          <div class="text-semi text-16">
                                            Add to order
                                            <span v-if="getWrestlerUpsellPrice(wrestler.id) !== ''">&nbsp;-&nbsp;{{ getWrestlerUpsellPrice(wrestler.id) }}</span>
                                          </div>
                                        </div>
                                      </div>
                                      <div v-if="upsellRequired">
                                        <p class="m-y-none text-semi">This add-on is required to complete registration</p>
                                      </div>
                                      <div v-else
                                           class="card-bordered donation-card upsell-decline-card m-y-m flex gap-x-sm"
                                           :class="wrestlerUpsells[wrestler.id].addedToCart === false ? 'border-1 border-gray-600' : pillStyles"
                                           @click.prevent="wrestlerUpsells[wrestler.id].addedToCart = false">
                                        <div>
                                          <input type="radio" :name="`upsell-decision-${wrestler.id}`" :value="false" v-model="wrestlerUpsells[wrestler.id].addedToCart">
                                        </div>
                                        <div class="flex flex-col">
                                          <div class="text-16">
                                            No, thank you
                                          </div>
                                        </div>
                                      </div>
                                      <!-- Add-ons configured on the upsold product itself (e.g. custom name printing) -->
                                      <div v-if="wrestlerUpsells[wrestler.id].addedToCart && wrestlerUpsells[wrestler.id].selectedBillingPlan && planAddOnsFor(wrestler.id).length > 0" class="m-t-m">
                                        <div v-for="addOn in planAddOnsFor(wrestler.id)"
                                             :key="`add-on-${wrestler.id}-${addOn.upsell.id}`"
                                             class="card-bordered p-sm m-y-sm">
                                          <div class="flex gap-x-sm align-items-start">
                                            <div>
                                              <input type="checkbox"
                                                     :id="`add-on-${wrestler.id}-${addOn.upsell.id}`"
                                                     :checked="addOnIncluded(wrestler.id, addOn)"
                                                     :disabled="addOn.required"
                                                     @change="toggleWrestlerAddOn(wrestler.id, addOn)">
                                            </div>
                                            <div class="flex flex-col w-full">
                                              <label :for="`add-on-${wrestler.id}-${addOn.upsell.id}`" class="text-semi m-b-none">
                                                {{ addOn.upsell.name }}
                                                <span v-if="addOn.required" class="text-12 text-red-600 uppercase m-l-xxs">Required</span>
                                                <span v-if="addOn.upsell.amount > 0" class="m-l-xxs">+{{ formatAsCurrency(addOn.upsell.amount, 2) }}</span>
                                              </label>
                                              <p v-if="addOn.upsell.description" class="text-gray-600 m-b-xxs">{{ addOn.upsell.description }}</p>
                                              <ValidationProvider v-if="addOnIncluded(wrestler.id, addOn) && addOn.upsell.require_user_input"
                                                                  :name="`${addOn.upsell.input_label || addOn.upsell.name} for ${wrestler.first_name}`"
                                                                  rules="required"
                                                                  v-slot="{ errors }">
                                                <label class="text-12 m-t-xxs m-b-none">{{ addOn.upsell.input_label }}</label>
                                                <input type="text"
                                                       :class="inputClasses(errors[0])"
                                                       v-model="wrestlerUpsells[wrestler.id].addOnInputs[addOn.upsell.id]">
                                                <p class="m-t-xxs text-red-600" v-if="errors[0]">
                                                  Please enter {{ addOn.upsell.input_label || addOn.upsell.name }} for {{ wrestler.first_name }} to continue.
                                                </p>
                                              </ValidationProvider>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                      <p class="m-t-xxs text-red-600" v-if="errors[0]">
                                        Please accept or decline this add-on for {{ wrestler.first_name }} to continue.
                                      </p>
                                    </form>
                                  </div>
                                </ValidationProvider>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                </div>
                <div v-if="showGapMedicalInsurance || showSeasonInterruptionInsurance" class="ibox">
                  <div class="ibox-content relative">
                    <div
                        class="absolute w-full bg-gray-200 rounded-t-xxs"
                        style="top: 0; left: 0;"
                    >
                      <div class="upper-label text-14 text-heavy p-y-sm p-l-md text-gray-800">
                        Protect your experience
                      </div>
                    </div>
                    <p class="p-t-xxl">
                      Consider adding insurance to protect your investment in case of injury or cancellation.
                    </p>
                    <div class="m-t-md" v-if="showSeasonInterruptionInsurance">
                      <ValidationProvider v-slot="{ validate, errors }" ref="seasonInterruptionProvider"
                                          rules="required|insuranceOffer">
                        <season-interruption
                            :client-id="vertClientId"
                            :customer-email-address="email"
                            :customer-first-name="customerInfo.first_name"
                            :customer-last-name="customerInfo.last_name"
                            :customer-street-address="addressLine1"
                            :customer-city="city"
                            :customer-state="state"
                            :customer-postal-code="zip"
                            :data-context-name="teamName"
                            :data-context-event="session.name"
                            :insurable-amount="insurableTotal"
                            :event-start-date="startDateForInsuranceOffer"
                            :event-end-date="session.end_at"
                            :participant-first-name="coveredWrestler.first_name"
                            :participant-last-name="coveredWrestler.last_name"
                            @offer-state-change="insuranceOptionPicked"
                        ></season-interruption>
                        <p class="m-t-xxs text-red-600" v-if="errors[0]">
                          Please accept or decline season interruption insurance.
                        </p>
                      </ValidationProvider>
                    </div>
                    <div class="m-t-md" v-if="showGapMedicalInsurance">
                      <ValidationProvider v-slot="{ validate, errors }" ref="gapMedicalProvider"
                                          rules="required|insuranceOffer">
                        <gap-medical
                            :client-id="vertClientId"
                            :customer-email-address="email"
                            :customer-first-name="customerInfo.first_name"
                            :customer-last-name="customerInfo.last_name"
                            :customer-street-address="addressLine1"
                            :customer-city="city"
                            :customer-state="state"
                            :customer-postal-code="zip"
                            :coverage-start-date="startDateForInsuranceOffer"
                            :coverage-end-date="session.end_at"
                            coverage-type="WRESTLING"
                            :data-context-name="teamName"
                            :participant-first-name="coveredWrestler.first_name"
                            :participant-last-name="coveredWrestler.last_name"
                            :participant-state="state"
                            :participant-birth-date="formattedDateOfBirth"
                            @offer-state-change="insuranceOptionPicked"
                        ></gap-medical>
                        <p class="m-t-xxs  text-red-600" v-if="errors[0]">
                          Please accept or decline gap medical insurance.
                        </p>
                      </ValidationProvider>
                    </div>
                  </div>
                </div>
              </div>
              <div id="payment-details" class="ibox" v-else-if="showingPay">
                <div class="ibox-content">
                  <h1 class="p-t-m m-t-none font-heavy">Payment</h1>
                  <div class="panel-group" id="accordion">
                    <div v-if="free">
                      <div class="panel panel-success">
                        <div class="panel-heading">
                          <h5 class="panel-title">Free</h5>
                        </div>
                        <div class="panel-body">
                          <p>
                            Tap the register button to complete your free registration.
                          </p>
                          <p v-if="paid">
                            Thank you for registering! Check your email for your
                            receipt and additional information.
                          </p>
                        </div>
                      </div>
                    </div>
                    <div v-else>
                      <div :class="cashSelected ? 'panel panel-success' : 'panel panel-white'"
                           v-if="allowsCash() && acceptedInsuranceOffers.length === 0">
                        <div class="panel-heading" v-on:click="cashTapped()">
                          <h5 class="panel-title">
                            <label>
                              <input
                                  type="radio"
                                  value="cash"
                                  id="cash_payment"
                                  v-model="paymentMethod"
                              />&nbsp; Cash or check
                            </label>
                          </h5>
                        </div>
                        <div v-show="cashSelected">
                          <div class="panel-body">
                            <p>
                              Payment will be due at the next practice or event for
                              this session.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div :class="creditSelected && allowsCash() ? 'panel panel-success' : ''">
                        <div class="panel-heading" v-on:click="creditTapped()" v-if="allowsCash()">
                          <h5 class="panel-title">
                            <label>
                              <input
                                  type="radio"
                                  value="card"
                                  id="credit_payment"
                                  v-model="paymentMethod"
                              />&nbsp; Credit or debit
                            </label>
                          </h5>
                        </div>
                        <div v-show="creditSelected">
                          <div id="credit-body" :class="creditSelected && allowsCash() ? 'panel-body' : ''">
                            <div v-if="hasSavedCard && !updatingSavedCard">
                              <div class="flex align-items-top justify-space-between m-t-sm">
                                <h3 class="font-semi m-t-none m-b-none" style="margin-top: 0; margin-bottom: 0;">Saved Card</h3>
                                <a @click.prevent="updatingSavedCard = true" class="flex align-items-baseline gap-x-xs">
                                  <i class="fa-solid fa-pencil"/>
                                  <span class="">Edit card</span>
                                </a>
                              </div>
                              <div class="flex flex-wrap gap-x-sm align-items-baseline m-t-xs">
                                <div>
                                  <input type="radio" value="saved-card" checked>
                                </div>
                                <div class="flex align-items-center gap-x-sm">
                                  <div v-html="cardLogo"></div>
                                  <div class="font-heavy">* * * * {{
                                      billingProfile.default_payment_method_last4
                                    }}
                                  </div>
                                  <div class="">
                                    Expires {{billingProfile.default_payment_method_exp_month }} / {{ billingProfile.default_payment_method_exp_year }}
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- Display a payment form -->
                            <form v-else role="form" id="payment-form">
                              <a v-if="hasSavedCard && updatingSavedCard" @click.prevent="updatingSavedCard = false" class="flex align-items-baseline gap-x-xs m-t-sm m-b-lg">
                                <i class="fa-solid fa-arrow-rotate-left"/>
                                <span class="">Cancel - use saved card</span>
                              </a>
                              <div id="payment-element" v-if="useStripe">
                                <!--Stripe.js injects the Payment Element-->
                              </div>
                              <div v-else-if="useJustifi">
                                <!-- Show expiration warning if approaching 15 minute mark -->
                                <div v-if="showExpirationWarning && !paymentExpired" class="m-b-sm p-xs bg-yellow-50 border border-solid border-yellow-200 rounded-md">
                                  <div class="flex align-items-center gap-sm">
                                    <svg class="h-6 w-6 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                      <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                    </svg>
                                    <div class="flex-1">
                                      <p class="font-semi text-yellow-800">
                                        Payment form expires in {{ formattedTimeRemaining }}
                                      </p>
                                      <p class="text-yellow-700 m-t-xs">
                                        Please complete your payment before the timer expires.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <!-- Show expired message if payment has expired -->
                                <div v-if="paymentExpired" class="p-xs bg-red-50 border border-solid border-red-200 rounded-md">
                                  <div class="flex align-items-center gap-sm">
                                    <svg class="h-6 w-6 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                      <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                                    </svg>
                                    <div>
                                      <p class="font-semi text-red-800">
                                        Cart expired
                                      </p>
                                      <p class="text-red-700 m-t-xs">
                                        Please refresh the page to enter your card details.
                                      </p>
                                      <button @click="reloadPageForExpiration" type="button" class="btn btn-danger">
                                        Refresh Page
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                <div  style="margin-top: -32px;">
                                  <justifi-tokenize-payment-method
                                    :account-id="billingAcctId"
                                    :auth-token="intentSecret"
                                    disable-bank-account="true"
                                    hide-submit-button="true"
                                    hide-card-billing-form="true"
                                  >
                                  </justifi-tokenize-payment-method>
                                </div>
                              </div>
                            </form>
                            <p class="m-t-m">
                              <svg class="h-4 w-4 text-gray-500 inline" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd"
                                      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                      clip-rule="evenodd"/>
                              </svg>
                              Secure payment processing provided by <span v-if="useStripe">Stripe</span><span v-else>WrestlingIQ</span>
                            </p>
                            <p v-if="paid">
                              Thank you for registering! Check your email for your
                              receipt and additional information.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="col-xs-12 col-md-8 col-md-offset-2 col-lg-3 col-lg-offset-0" v-if="chosenPlanOrPaymentOption || showingDivisions">
              <div id="summary-card" class="affix" data-offset-top="0">
                <div class="ibox p-x-m">
                  <div class="ibox-content">
                    <div>
                      <div>
                        <div class="flex flex-col">
                          <h1 class="m-t-none m-b-none-important p-t-md">Summary</h1>
                          <h2 class="m-b-m">
                            <span v-if="recurringSession">Subscription</span>
                            <span v-else-if="isTeamRegistration">
                              Application
                            </span>
                            <span v-else>
                              Registration
                            </span>
                            for {{ session.name }}
                          </h2>
                          <!-- for one time registrations -->
                          <spinner v-if="loadingPreview && !showingDivisions"></spinner>
                          <!-- team registration: unified per-wrestler sidebar (divisions step + checkout) -->
                          <template v-if="isTeamRegistration && !loadingPreview">
                            <!-- Before preview: show wrestlers with division status -->
                            <template v-if="checkouts.length === 0">
                              <div class="m-b-xs" v-for="wrestler in selectedWrestlers" :key="'sidebar-' + wrestler.id">
                                <p class="m-b-none font-semi">{{ wrestler.first_name }} {{ wrestler.last_name }}</p>
                                <p v-if="hasDivisionSelection && wrestlerDivisions[wrestler.id] && wrestlerDivisions[wrestler.id].selectedAgeDivisionId"
                                   class="m-b-none text-12 text-gray-600">
                                  {{ getDivisionDisplayName(wrestlerDivisions[wrestler.id].selectedAgeDivisionId) }}
                                  <span v-if="wrestlerDivisions[wrestler.id].selectedWeightClassId">
                                    - {{ getWeightClassDisplayName(wrestlerDivisions[wrestler.id].selectedAgeDivisionId, wrestlerDivisions[wrestler.id].selectedWeightClassId) }}
                                  </span>
                                </p>
                                <p v-else-if="hasDivisionSelection" class="m-b-none text-12 text-gray-400">
                                  Division not selected
                                </p>
                              </div>
                            </template>
                            <!-- After preview: show wrestlers with division + charges -->
                            <template v-else>
                              <div :class="{ 'm-t-m': cIndex > 0 }"
                                   v-for="(checkout, cIndex) in checkouts"
                                   :key="'sidebar-' + getCheckoutWrestlerId(checkout)">
                                <p class="m-b-none font-semi">
                                  {{ getCheckoutWrestlerName(checkout) }}
                                </p>
                                <p v-if="hasDivisionSelection && wrestlerDivisions[getCheckoutWrestlerId(checkout)] && wrestlerDivisions[getCheckoutWrestlerId(checkout)].selectedAgeDivisionId"
                                   class="m-b-xs text-12 text-gray-600">
                                  {{ getDivisionDisplayName(wrestlerDivisions[getCheckoutWrestlerId(checkout)].selectedAgeDivisionId) }}
                                  <span v-if="wrestlerDivisions[getCheckoutWrestlerId(checkout)].selectedWeightClassId">
                                    - {{ getWeightClassDisplayName(wrestlerDivisions[getCheckoutWrestlerId(checkout)].selectedAgeDivisionId, wrestlerDivisions[getCheckoutWrestlerId(checkout)].selectedWeightClassId) }}
                                  </span>
                                </p>
                                <div v-for="(invoice, iIndex) in checkout.manual_invoices" :key="iIndex">
                                  <div
                                    class="flex flex-wrap justify-space-between gap-x-sm m-t-xxs"
                                  >
                                    <p class="m-b-xxs">
                                      {{ teamRegDue(invoice) }}
                                    </p>
                                    <p class="m-b-none text-right">
                                      {{ formatAsCurrency(invoice.amount_due) }}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </template>
                          </template>
                          <!-- non-team: flat invoice list in sidebar -->
                          <div class="m-b-xs" v-for="(invoice, index) in invoices" v-if="!isTeamRegistration && !loadingPreview">
                            <template v-if="invoices.length > 1">
                              <div
                                  class="flex flex-wrap justify-space-between gap-x-sm m-b-xxs"
                              >
                                <p class="m-b-xxs font-semi">
                                  {{ due(invoice) }}
                                </p>
                                <p class="m-b-none text-right">
                                  {{ formatAsCurrency(invoice.amount_due) }}
                                </p>
                              </div>
                            </template>
                            <template v-else>
                              <div
                                  class="flex flex-wrap justify-space-between gap-x-sm m-b-xxs"
                                  v-for="(item, index) in invoice.invoice_items"
                              >
                                <p class="m-b-none">
                                  {{ shortDescription(item) }}
                                </p>
                                <p class="m-b-none text-right">
                                  {{ formatAsCurrency(item.amount) }}
                                </p>
                              </div>
                            </template>
                          </div>
                          <!-- for subscriptions -->
                          <template v-if="selectedBillingPlan && previewRequest && !loadingPreview">
                            <div v-if="classPassLabel(selectedBillingPlan)" class="text-12 inline-flex align-items-center gap-x-xxs m-b-sm">
                              <i class="fa-solid fa-ticket"></i> {{ classPassLabel(selectedBillingPlan) }}
                            </div>
                            <div class="m-b-m" v-html="planDescription(selectedBillingPlan)">
                            </div>
                            <div class="-m-t-sm m-b-xs" v-if="selectedWrestlers.length > 1">You are signing up for
                              <span class="font-semi">{{ selectedWrestlers.length }} recurring charges</span>, one for
                              each wrestler.
                            </div>
                            <div>
                              <hr/>
                            </div>
                            <div :class="{ 'm-t-md': index > 0 }"
                                 v-for="(subRequest, index) in subscriptionsRequested">
                              <p class="m-b-none font-semi m-b-sm text-16">
                                {{ subRequest.wrestler_profile.first_name }}
                                {{ subRequest.wrestler_profile.last_name }}
                              </p>
                              <div v-for="plan in subRequest.plan_requests"
                                   class="flex justify-space-between align-items-baseline">
                                <p class="m-b-none">{{ plan.billing_plan.name }}</p>
                                <p class="m-b-none flex-shrink-0">
                                  {{ formatAsCurrency(plan.billing_plan.amount) }}
                                </p>
                              </div>
                              <div v-if="subRequest.coupon != null"
                                   class="flex justify-space-between align-items-baseline m-t-xs">
                                <div class="m-b-none flex align-items-center flex-wrap gap-x-xs">
                                  <p class="m-b-none">
                                    Scholarship
                                  </p>
                                  <div
                                      class="text-sm bg-gray-50 text-gray-600 rounded-xxs flex gap-x-xs align-items-center"
                                      style="padding: 2px 4px;">
                                    <div>
                                      {{ subRequest.coupon.code }}
                                    </div>
                                    <i class="fa-solid fa-xmark text-gray-300 cursor-pointer"
                                       @click="removeCoupon(subRequest)"></i>
                                  </div>
                                </div>
                                <p class="m-b-none flex-shrink-0">
                                  - {{ formatAsCurrency(subRequest.discount_total) }}
                                </p>
                              </div>
                              <div class="m-t-sm flex justify-space-between align-items-baseline text-heavy">
                                <p class="m-b-none font-semi text-heavy text-14">Recurring total for
                                  {{ subRequest.wrestler_profile.first_name }}</p>
                                <p class="m-b-none flex-shrink-0 text-14">
                                  {{ formatAsCurrency(subRequest.total_per_interval) }} {{ planPriceExtra(subRequest) }}
                                </p>
                              </div>
                              <div v-if="deferredBillingDate && subRequest.one_time_fee_total > 0">
                                <hr/>
                                <div class="m-t-m flex justify-space-between align-items-baseline">
                                  <p class="m-b-none">One-time Registration Fee</p>
                                  <p class="m-b-none flex-shrink-0">
                                    {{ formatAsCurrency(subRequest.one_time_fee_total) }}
                                  </p>
                                </div>
                              </div>
                              <div v-else-if="deferredBillingDate">
                                <hr/>
                                <div class="m-t-sm flex justify-space-between align-items-baseline text-heavy">
                                  <p class="m-b-none font-semi text-heavy text-14">
                                    Due today
                                  </p>
                                  <p class="m-b-none flex-shrink-0 text-14">
                                    {{ formatAsCurrency(0) }}
                                  </p>
                                </div>
                              </div>
                              <div v-else-if="subRequest.one_time_fee_total > 0">
                                <hr/>
                                <div class="m-t-m flex justify-space-between align-items-baseline">
                                  <p class="m-b-none">One-time Registration Fee</p>
                                  <p class="m-b-none flex-shrink-0">
                                    {{ formatAsCurrency(subRequest.one_time_fee_total) }}
                                  </p>
                                </div>
                                <div v-if="anchorDayOfMonth" class="m-t-sm flex justify-space-between align-items-baseline">
                                  <p class="m-b-none text-14">
                                    Prorated first charge for {{ subRequest.wrestler_profile.first_name }}
                                  </p>
                                  <div v-if="subRequest.total_due_today !== subRequest.total_per_interval">
                                    <p class="m-b-none text-14">
                                      {{ formatAsCurrency(subRequest.total_due_today) }}
                                    </p>
                                  </div>
                                  <p v-else class="m-b-none flex-shrink-0 text-14">
                                    {{ formatAsCurrency(subRequest.total_per_interval) }}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </template>
                        </div>
                        <template v-if="!showingDivisions">
                          <hr/>
                          <div v-if="acceptedInsuranceOffers.length > 0">
                            <h2 class="-m-t-m m-b-none-important p-t-md">Insurance Coverage</h2>
                            <p class="m-t-sm m-b-md text-12">
                              Insurance will show up as a separate charge on your card. You will receive a separate email
                              with policy documents.
                            </p>
                            <div
                                class="flex flex-wrap justify-space-between gap-x-sm m-b-xxs"
                                v-for="offer in acceptedInsuranceOffers">
                              <p class="m-b-none-important">
                                {{ offer.product.friendly_name }}
                              </p>
                              <p class="m-b-none text-right">
                                {{ formatAsCurrency(offer.total) }}
                              </p>
                            </div>
                            <hr/>
                          </div>
                          <div v-if="isTeamRegistration" class="m-b-m">
                            <div v-if="selectedDepositAmount > 0" class="alert bg-blue-50 m-b-sm">
                              <p class="m-b-xxs font-semi text-14">
                                <i class="fa-solid fa-circle-info m-r-xxs"></i> An application fee of {{ formatAsCurrency(selectedDepositAmount) }} per wrestler will be charged now.
                              </p>
                              <p class="m-b-none text-14">
                                The remaining balance will only be charged if your application is approved.
                              </p>
                            </div>
                            <div v-else class="alert bg-blue-50 m-b-sm">
                              <p class="m-b-xxs font-semi text-14">
                                <i class="fa-solid fa-circle-info m-r-xxs"></i> Your card will not be charged now.
                              </p>
                              <p class="m-b-none text-14">
                                Payment information is collected to reserve your spot. Your card will only be charged if your application is approved.
                              </p>
                            </div>
                            <div class="justify-space-between flex">
                              <h2 class="text-18 m-t-none m-b-none-important font-heavy">
                                {{ selectedDepositAmount > 0 ? 'Total' : 'Total if accepted' }}
                              </h2>
                              <p class="text-18 m-t-none font-heavy m-b-none text-right">
                                <spinner v-if="loadingPreview"></spinner>
                                <template v-if="!loadingPreview">
                                  {{ total(acceptedInsuranceTotal) }}
                                </template>
                              </p>
                            </div>
                            <div v-if="selectedDepositAmount > 0" class="justify-space-between flex m-t-xxs">
                              <p class="text-14 font-semi m-b-none">
                                Due now (deposit)
                              </p>
                              <p class="text-14 font-semi m-b-none text-right">
                                <template v-if="!loadingPreview">
                                  {{ installmentTotalDueNow(acceptedInsuranceTotal) }}
                                </template>
                              </p>
                            </div>
                          </div>
                          <div v-else class="justify-space-between flex m-b-m">
                            <h2 class="text-18 m-t-none m-b-none-important font-heavy">
                              Due now
                            </h2>
                            <p class="text-18 m-t-none font-heavy m-b-none text-right">
                              <spinner v-if="loadingPreview"></spinner>
                              <template v-if="recurringSession && !loadingPreview">
                                {{ totalSubscriptionPrice }}
                              </template>
                              <template v-else-if="!loadingPreview">
                                {{
                                  selectedPaymentOption.payment_strategy === 'installments' ? installmentTotalDueNow(acceptedInsuranceTotal) : total(acceptedInsuranceTotal)
                                }}
                              </template>
                            </p>
                          </div>
                        </template>
                        <div v-if="deferredBillingDate">
                          <div class="alert bg-blue-50 text-14 font-semi m-t-xs">
                            <i class="fa-solid fa-circle-info m-r-xxs"></i>
                            <template v-if="deferredHasOneTimeFee">
                              Your one-time registration fee will be charged today. Recurring billing begins on {{ deferredBillingDate }}.
                            </template>
                            <template v-else>
                              Your card will not be charged today. Billing begins on {{ deferredBillingDate }}.
                            </template>
                          </div>
                        </div>
                        <div v-else-if="anchorDayOfMonth">
                          <div class="alert bg-green-50 text-14 font-semi m-t-xs">
                            <i class="fa-solid fa-circle-info m-r-xxs"></i> Bills on the {{ anchorDayOfMonth }} each month. Today's charge covers the prorated period until your first full payment on the {{ anchorDayOfMonth }}.
                          </div>
                        </div>
                        <ladda-button
                            cypress-class="register-next-btn"
                            @lbClicked="handleSubmit(nextStep)"
                            el-class="btn-primary block w-full"
                            el-identifier="register-next-btn"
                            :disabled="buttonDisabled"
                            :loading="buttonLoading"
                        >
                          {{ summaryCTA }}
                        </ladda-button>
                        <p v-if="failed" data-cy="validation-error-text" class="text-red-500 text-12 text-center py-2 m-t-xs">{{
                            validateFailedMsg
                          }}</p>
                        <p v-if="error" data-cy="error-text" class="text-red-500 text-12 text-center py-2 m-t-xs">{{
                            errorMessage
                          }}</p>
                        <div class="flex justify-space-between align-items-center py-2 m-t-xs">
                          <a @click="previousStep" v-if="currentStep !== 'review' && currentStep !== 'divisions'">
                            <div class="flex gap-x-xxs align-items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor"
                                   class="w-4 h-4">
                                <path fill-rule="evenodd"
                                      d="M14 8a.75.75 0 0 1-.75.75H4.56l1.22 1.22a.75.75 0 1 1-1.06 1.06l-2.5-2.5a.75.75 0 0 1 0-1.06l2.5-2.5a.75.75 0 0 1 1.06 1.06L4.56 7.25h8.69A.75.75 0 0 1 14 8Z"
                                      clip-rule="evenodd"/>
                              </svg>
                              <p class="text-600 text-12 m-t-none m-b-none">Back</p>
                            </div>
                          </a>
                          <p class="text-gray-600 text-12 m-t-none m-b-none"
                             :class="(currentStep === 'review' || currentStep === 'divisions') ? 'text-center w-full' : ''">Step {{ currentStepCount }}
                            of {{ totalSteps }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </div>
  </div>
</template>
<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { TheMask } from 'vue-the-mask';
import swal from 'sweetalert2';
import Spinner from './spinner.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import { formattingMixin } from './mixins/formatting_mix';
import { funnelMixin } from './mixins/funnel_mix';
import { paymentOptionsMix } from './mixins/payment_options_mix';
import { billingPlanMix } from './mixins/billing_plan_mix';
import { errorableMix } from './mixins/errorable_mix';
import { billingPartnerMix } from '../../shared/billing_partner_mix';
import WrestlerIcon from '../../shared/wrestler-icon.vue';
import DivisionSelectionStep from './components/division_selection_step.vue';

export default {
  name: 'membership-form',
  mixins: [formattingMixin, funnelMixin, paymentOptionsMix, billingPlanMix, errorableMix, billingPartnerMix],
  components: {
    WrestlerIcon,
    LaddaButton,
    Spinner,
    ValidationProvider,
    ValidationObserver,
    TheMask,
    DivisionSelectionStep,
  },
  props: {
    guardianId: {
      type: Number,
      default: null,
    },
    guardianType: {
      type: String,
      default: null,
    },
    // Information about the billing customer
    customerInfo: {
      type: Object,
      required: true,
    },
    funnel: {
      type: Boolean,
      default: true,
    },
    teamId: {
      type: Number,
      required: true,
    },
    teamName: {
      type: String,
      required: true,
    },
    showDonation: {
      type: Boolean,
      default: false,
    },
    redirect: {
      type: String,
      default: '/calendar',
    },
    selectedWrestlers: {
      type: Array,
      required: true,
    },
    session: {
      type: Object,
      required: true,
    },
    allowSeasonInterruptionInsurance: {
      type: Boolean,
      default: false,
    },
    allowGapMedicalInsurance: {
      type: Boolean,
      default: false,
    },
    vertClientId: {
      type: String,
      default: null,
    },
    brandColor: {
      type: String,
      default: '#27AA83',
    },
    brandTextColor: {
      type: String,
      default: '#FFF',
    },
    customUpsellProduct: {
      type: Object,
    },
  },
  data() {
    return {
      loading: false,
      selectedBillingPlanId: null, // recurring subscriptions
      wrestlerCoupons: {
        // key is wrestler id, payload is coupon code string
        // used for recurring subscription auto application
      },
      wrestlerDivisions: {
        // key is wrestler id, value is { selectedAgeDivisionId, selectedWeightClassId }
        // used for team registrations with division standards
      },
      previewRequest: null,
      currentStep: 'review', // review, upsells, pay
      loadingPreview: false,
      registering: false,
      savingBillingInfo: false,
      creatingCharge: false,
      creatingToken: false,
      creatingSubscription: false,
      card: null,
      updatingSavedCard: false,
      paymentMethod: 'card',
      previewUrl: this.session.session_type === 'team_registration'
        ? this.$apiService.checkoutPreviewUrl()
        : this.$apiService.paymentPlanPreviewUrl(),
      insurance: {
        gapMedical: {
          state: 'NOT_RENDERED', // NOTRENDERED ACCEPTED, DECLINED, UNKNOWN
          quote: null,
          forceHide: false,
        },
        seasonInterruption: {
          state: 'NOT_RENDERED',
          quote: null,
          forceHide: false,
        },
      },
      wrestlerUpsells: {
        // key is wrestler id, value is upsell selection object
        // { addedToCart: null, selectedBillingPlan: null, activeSize: null, activeColor: null }
      },
      upsellPhotos: [],
      activeUpsellPhoto: null,
      // Button styles for upsells
      selectedBtnStyles: {
        color: this.brandTextColor,
        backgroundColor: this.brandColor,
      },
      btnStyles: {
        color: '#324D67', // text-gray-700
        backgroundColor: 'white',
      },
      selectedPillStyles: 'border-2 border-green-500',
      pillStyles: 'border border-gray-200',
      checkboxStyles: {
        accentColor: this.brandColor, // accentColor is broken due to tailwind forms plugin. For now we hard code color if tick box is not white.
        color: this.brandTextColor === '#FFF' ? this.brandColor : '#2563eb',
      },

      paid: false,
    };
  },
  computed: {
    startDateForInsuranceOffer() {
      if (this.session.start_at) {
        const sessionStart = moment(this.session.start_at);
        const today = moment();

        // If session start date is today or in the past, return tomorrow
        if (sessionStart.isSameOrBefore(today, 'day')) {
          return today.add(1, 'day').format('YYYY-MM-DD');
        }
      }

      // Otherwise just return whatever we have from the api/props
      return this.session.start_at;
    },
    acceptedInsuranceOffers() {
      const offers = [];

      if (this.insurance.gapMedical.state === 'ACCEPTED') {
        offers.push(this.insurance.gapMedical.quote);
      }

      if (this.insurance.seasonInterruption.state === 'ACCEPTED') {
        offers.push(this.insurance.seasonInterruption.quote);
      }

      return offers;
    },
    acceptedInsuranceTotal() {
      let total = 0;

      this.acceptedInsuranceOffers.forEach((offer) => {
        total += offer.total;
      });

      return total;
    },
    summaryCTA() {
      if (this.registrationIsClosed) {
        return 'Registration is closed';
      }

      switch (this.currentStep) {
        case 'divisions':
          return 'Continue';
        case 'review':
          return 'Continue';
        case 'upsells':
          return 'Proceed to Checkout';
        case 'pay':
          if (this.isTeamRegistration) {
            return 'Submit Application';
          }
          if (this.deferredBillingDate && !this.deferredHasOneTimeFee) {
            return 'Confirm Membership';
          }

          if (this.free || this.cashSelected) {
            return 'Register';
          }

          return 'Register & Pay';
      }
    },
    totalSteps() {
      let steps = 2; // review + pay
      if (this.hasDivisionSelection) {
        steps += 1;
      }
      if (this.hasUpsells) {
        steps += 1;
      }
      return steps;
    },
    currentStepCount() {
      // Order: divisions (if applicable) -> review -> upsells (if applicable) -> pay
      let stepNumber = 0;

      if (this.hasDivisionSelection) {
        stepNumber += 1;
        if (this.currentStep === 'divisions') {
          return stepNumber;
        }
      }

      stepNumber += 1;
      if (this.currentStep === 'review') {
        return stepNumber;
      }

      if (this.hasUpsells) {
        stepNumber += 1;
        if (this.currentStep === 'upsells') {
          return stepNumber;
        }
      }

      stepNumber += 1;
      return stepNumber; // pay
    },
    // V1 hack, Vertical Insurance only has one participant
    showGapMedicalInsurance() {
      return this.useJustifi && this.allowGapMedicalInsurance && this.selectedWrestlers.length === 1 && this.vertClientId && !this.free && !this.insurance.gapMedical.forceHide;
    },
    showSeasonInterruptionInsurance() {
      return this.useJustifi && this.allowSeasonInterruptionInsurance && this.selectedWrestlers.length === 1 && this.vertClientId && !this.free && !this.insurance.seasonInterruption.forceHide;
    },
    coveredWrestler() {
      return this.selectedWrestlers[0];
    },
    formattedDateOfBirth() {
      return moment(this.coveredWrestler.dob)
        .format('YYYY-MM-DD');
    },
    insurableTotal() {
      if (!this.pendingPlan) {
        return 0;
      }

      let total = 0;

      this.pendingPlan.manual_invoices.forEach((mi) => {
        total += mi.amount_due;
      });

      return total;
    },
    hasCustomUpsellProduct() {
      // A product whose SKUs have all been retired is treated as absent so a required
      // add-on can't dead-end the registrant (no plan to select, no decline option).
      return Object.keys(this.customUpsellProduct).length !== 0
        && this.activeUpsellBillingPlans.length > 0;
    },
    upsellRequired() {
      return this.session.upsell_required && this.hasCustomUpsellProduct;
    },
    hasUpsells() {
      return (this.showDonation && !this.recurringSession) || this.showGapMedicalInsurance || this.showSeasonInterruptionInsurance || this.hasCustomUpsellProduct;
    },
    isWrestleLikeAGirl() {
      return this.teamName.toLowerCase() === 'wrestle like a girl';
    },
    donationTiers() {
      if (this.isWrestleLikeAGirl) {
        return [
          {
            amount: 1000,
            example: "Supports Wrestle Like A Girl's efforts to sanction all 50 states.",
          },
          {
            amount: 2500,
            example: 'Provides one individual membership to the WLAG Collective, an on-line community designed by and focused on female wrestlers.',
          },
          {
            amount: 5000,
            example: "Covers one scholarship to WLAG's Academy nutrition course, \"Strong by Design\".",
          },
          {
            amount: 7500,
            example: "Supports one athlete's clinic registration fees.",
          },
          {
            amount: 10000,
            example: "Covers attendance & lunch for one athlete at the Girls' and Women's Summit, a one-day conference featuring specific topics for female wrestlers.",
          },
          {
            amount: 25000,
            example: "Covers a student-athlete seat at the Wrestle Like A Girl Gala, the annual celebration of women's wrestling.",
          },
        ];
      }
      return [
        {
          amount: 2500,
          example: 'Buys a workout shirt for one child in need.',
        },
        {
          amount: 5000,
          example: 'Provides a pair of headgear to a wrestler.',
        },
        {
          amount: 10000,
          example: 'Covers the cost of two pairs of shoes.',
        },
        {
          amount: 20000,
          example: 'Supports a wrestler for the entire season.',
        },
      ];
    },
    showingDivisions() {
      return this.currentStep === 'divisions';
    },
    showingReview() {
      return this.currentStep === 'review';
    },
    showingUpsells() {
      return this.currentStep === 'upsells';
    },
    showingPay() {
      return this.currentStep === 'pay';
    },
    hasDivisionSelection() {
      // Team registration with division standard enabled
      return (
        this.session.session_type === 'team_registration'
        && this.session.division_standard_id
        && this.session.division_standard
        && this.session.enabled_age_division_ids
        && this.session.enabled_age_division_ids.length > 0
      );
    },
    allDivisionSelectionsComplete() {
      if (!this.hasDivisionSelection) {
        return true;
      }
      return this.selectedWrestlers.every((wrestler) => {
        const selection = this.wrestlerDivisions[wrestler.id];
        return selection?.selectedAgeDivisionId && selection?.selectedWeightClassId;
      });
    },
    buttonDisabled() {
      if (this.registrationIsClosed) {
        return true;
      }
      if (this.showingDivisions) {
        return !this.allDivisionSelectionsComplete;
      }
      return this.savingBillingInfo || this.creatingCharge || this.creatingToken || this.creatingSubscription;
    },
    buttonLoading() {
      if (this.showingDivisions) {
        return false;
      }
      return this.registering || this.savingBillingInfo || this.creatingCharge || this.creatingToken || this.creatingSubscription || this.loadingPreview;
    },
    isTeamRegistration() {
      return this.session.session_type === 'team_registration';
    },
    recurringSession() {
      return this.session.product !== null;
    },
    activeBillingPlans() {
      if (this.recurringSession) {
        return _.filter(this.session.product.billing_plans, {
          active: true,
          is_wiq_fee: false,
          is_tax_item: false,
          is_one_time_fee: false,
        });
      }

      return [];
    },
    chosenPlanOrPaymentOption() {
      return this.selectedPaymentOption || this.selectedBillingPlan;
    },
    selectedDepositAmount() {
      return _.get(this.selectedPaymentOption, 'deposit_amount', 0) || 0;
    },
    hasMultiplePayments() {
      return this.installmentsPicked || this.selectedDepositAmount > 0;
    },
    selectedBillingPlan() {
      if (this.selectedBillingPlanId) {
        return _.find(this.activeBillingPlans, ['id', this.selectedBillingPlanId]);
      }

      return null;
    },
    anchorDayOfMonth() {
      if (!this.selectedBillingPlan) {
        return null;
      }

      const day = this.selectedBillingPlan.anchor_day_of_month;
      if (day === 1) {
        return '1st';
      } if (day === 15) {
        return '15th';
      }

      return null;
    },
    deferredBillingDate() {
      if (!this.selectedBillingPlan || !this.selectedBillingPlan.billing_starts_at) {
        return null;
      }

      const billingStartDate = moment(this.selectedBillingPlan.billing_starts_at);
      if (billingStartDate.isSameOrBefore(moment(), 'day')) {
        return null;
      }

      return billingStartDate.format('MMMM D, YYYY');
    },
    deferredHasOneTimeFee() {
      if (!this.deferredBillingDate) {
        return false;
      }
      return this.subscriptionsRequested.some((sub) => sub.one_time_fee_total > 0);
    },
    totalSubscriptionPrice() {
      if (!this.previewRequest) {
        return '––';
      }
      const subTotal = _.sum(_.map(this.subscriptionsRequested, 'total_due_today')) + _.sum(_.map(this.subscriptionsRequested, 'one_time_fee_total'));
      return (subTotal / 100.00).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      });
    },
    subscriptionsRequested() {
      if (!this.previewRequest) {
        return [];
      }

      return this.previewRequest.subscriptions_requested;
    },
    validateFailedMsg() {
      if (this.currentStep === 'divisions') {
        return 'Please select a division and weight class for each wrestler.';
      } if (this.currentStep === 'review') {
        return 'Please fill out your billing info to continue.';
      } if (this.currentStep === 'upsells') {
        return 'Please accept or decline all add-ons and fill out any required fields to continue.';
      } if (this.currentStep === 'pay') {
        return 'Please fix issues on the form to continue.';
      }
    },
    // Inactive SKUs (e.g. retired sizes/colors) must never surface as options or be selectable.
    // Mirrors availableBillingPlans in onlinestore/product.vue.
    activeUpsellBillingPlans() {
      return _.filter(this.customUpsellProduct.billing_plans, ['active', true]);
    },
    upsellColors() {
      return _.uniq(_.compact(_.flatMap(this.activeUpsellBillingPlans, 'color')));
    },
    upsellSizes() {
      const sizeMap = {
        YXXXS: 1,
        Y3XS: 2,
        Y2XS: 3,
        YXXS: 4,
        YXS: 5,
        YS: 6,
        YM: 7,
        YL: 8,
        YXL: 9,
        XXXS: 10,
        '3XS': 11,
        '2XS': 12,
        XXS: 13,
        XS: 14,
        S: 15,
        SM: 16,
        M: 17,
        L: 18,
        LG: 19,
        XL: 20,
        XXL: 21,
        '2XL': 22,
        '3XL': 23,
        '4XL': 24,
        BXXXS: 25,
        B3XS: 26,
        B2XS: 27,
        BXXS: 28,
        BXS: 29,
        BS: 30,
        BM: 31,
        BL: 32,
        BXL: 33,
        MXXXS: 34,
        M3XS: 35,
        M2XS: 36,
        MXXS: 37,
        MXS: 38,
        MS: 39,
        MM: 40,
        ML: 41,
        MXL: 42,
        M2XL: 43,
        M3XL: 44,
        M4XL: 45,
        WXXXS: 46,
        W3XS: 47,
        W2XS: 48,
        WXXS: 49,
        WXS: 50,
        WS: 51,
        WM: 52,
        WL: 53,
        WXL: 54,
        W2XL: 55,
        W3XL: 56,
        W4XL: 57,
        AXXXS: 58,
        A3XS: 59,
        A2XS: 60,
        AXXS: 61,
        AXS: 62,
        AS: 63,
        AM: 64,
        AL: 65,
        AXL: 66,
        A2XL: 67,
        AXXL: 68,
        A3XL: 69,
        A4XL: 70,
      };

      return _.sortBy(_.uniq(_.compact(_.flatMap(this.activeUpsellBillingPlans, 'size'))), (size) => sizeMap[size.toUpperCase()] || 100);
    },
    upsellPriceTiers() {
      // Price tiers are for products with multiple billing plans but no size/color variants
      // Examples: donation tiers ($10, $25, $50) or service levels (basic, premium, deluxe)
      if (!this.hasCustomUpsellProduct) {
        return [];
      }

      // If there are size or color options, don't show price tiers (those are handled separately)
      if (this.upsellSizes.length > 0 || this.upsellColors.length > 0) {
        return [];
      }

      // Return unique billing plans by ID (deduplicated), sorted by amount
      const plans = this.activeUpsellBillingPlans;
      const uniquePlans = _.uniqBy(plans, 'id');

      return _.sortBy(uniquePlans, 'amount').map((plan) => ({
        id: plan.id,
        name: plan.name,
        amount: plan.amount,
      }));
    },
    missingSelectionText() {
      const missing = [];
      if (this.upsellPriceTiers.length > 1) {
        return 'option above to ';
      }
      if (this.upsellColors.length > 0) {
        missing.push('color');
      }
      if (this.upsellSizes.length > 0) {
        missing.push('size');
      }
      return `${missing.join('/')} to see price and`;
    },
  },
  watch: {
    wrestlerUpsells: {
      handler() {
        // Only fire a preview of the charge if we are actively selecting stuff, not initializing.
        // Note: We no longer call findAndSetWrestlerUpsellPlan from the watcher because Vue 2's
        // deep watcher receives the same object reference for oldVal and val after mutations,
        // making reliable change detection impossible and causing infinite loops.
        // Instead, findAndSetWrestlerUpsellPlan is called directly from the setter methods.
        const paymentOptionId = _.get(this.selectedPaymentOption, 'id', null);
        if (paymentOptionId) {
          this.previewCharge(this.previewUrl);
        }
      },
      deep: true,
    },
    chosenPlanOrPaymentOption() {
      this.scrollTo('your-selection');
    },
    donationAmount() {
      this.previewCharge(this.previewUrl);
    },
    selectedBillingPlan(newPlan) {
      if (newPlan) {
        this.previewSubscription();
        // Check for duplicates
        this.checkForDuplicateSubs(newPlan);
      }
    },
    currentStep() {
      if (this.currentStep === 'upsells') {
        // Basically, if the insurance is not rendering in a reasonable time frame, we want to skip it.
        const vm = this;
        setTimeout(() => {
          if (vm.insurance.seasonInterruption.state === 'NOT_RENDERED') {
            vm.insurance.seasonInterruption.forceHide = true;
          }

          if (vm.insurance.gapMedical.state === 'NOT_RENDERED') {
            vm.insurance.gapMedical.forceHide = true;
          }

          // At this point, there is a chance we have no more upsells. If that is true, just move on.
          if (!vm.hasUpsells) {
            vm.nextStep();
          }
        }, 3000);
      }

      // Scroll
      if (this.currentStep === 'divisions') {
        this.scrollTo('divisions');
      } else if (this.currentStep === 'review') {
        this.scrollTo('review-order');
      } else if (this.currentStep === 'upsells') {
        this.scrollTo('upsells');
      } else if (this.currentStep === 'pay') {
        this.scrollTo('payment-details');
        if (this.useStripe && (!this.hasSavedCard || this.updatingSavedCard) && !this.free) {
          this.$forceNextTick(() => {
            this.setupPaymentElement(this.billingIntentSecret);
          });
        }
      }
    },
    updatingSavedCard() {
      if (this.updatingSavedCard && this.useStripe) {
        this.$forceNextTick(() => {
          this.setupPaymentElement(this.billingIntentSecret);
        });
      }
    },
    loadingPreview() {
      if (!this.loadingPreview) {
        this.checkForAffixOverflow();
      }
    },
    paymentMethod() {
      this.previewCharge(this.previewUrl);
    },
  },
  created() {
    // Debounce slightly to prevent request race conditions
    this.previewCharge = _.debounce(this.previewCharge, 150);
    if (this.recurringSession) {
      this.initWrestlerCouponMap();
      // If there is only 1 recurring plan, select it by default
      if (this.activeBillingPlans.length === 1) {
        this.selectedBillingPlanId = this.activeBillingPlans[0].id;
      }
    } else {
      // init custom upsells per wrestler
      this.initWrestlerUpsellMap();
      // init division selections for team registrations
      this.initWrestlerDivisionMap();
      // init per-wrestler coupon map for team registrations
      if (this.isTeamRegistration) {
        this.initWrestlerCouponMap();
      }
      // init upsell photos
      if (this.hasCustomUpsellProduct) {
        this.upsellPhotos = _.uniqBy(_.flatMap(this.activeUpsellBillingPlans, 'photos'), 'url');
        if (this.upsellPhotos.length === 0) {
          this.upsellPhotos = [{ url: 'https://mktg-cdn.wrestlingiq.com/placeholders/placeholder.png' }];
        }
        this.activeUpsellPhoto = _.head(this.upsellPhotos);
      }
      // If there is only one payment option, select it by default
      if (this.activePaymentOptions.length === 1) {
        this.selectOption(this.activePaymentOptions[0]);
      }
      // Set initial step to divisions if applicable
      if (this.hasDivisionSelection) {
        this.currentStep = 'divisions';
      }
    }
    // We always show coupon forms now instead of making them click
    this.showCouponForm = true;

    history.replaceState({ currentStep: this.currentStep }, '');
    window.addEventListener('popstate', this.popStateHandler);
  },
  destroyed() {
    window.removeEventListener('popstate', this.popStateHandler);
  },
  methods: {
    formatBillingStartDate(dateStr) {
      if (!dateStr) return null;
      const date = moment(dateStr);
      if (!date.isValid() || date.isSameOrBefore(moment(), 'day')) {
        return null;
      }
      return date.format('MMM D, YYYY');
    },
    checkForAffixOverflow() {
      // check for viewport issues so that we do not affix really big purchases and disable the purchase button
      this.$nextTick(() => {
        const element = document.getElementById('summary-card');
        if (element) {
          const rectHeight = element.getBoundingClientRect().height;
          const { innerHeight } = window;
          // If element inner height is > sreen height - 200, remove the affix class
          if (rectHeight > innerHeight - 250) {
            $('#summary-card').removeClass('affix');
          }
        }
      });
    },
    initWrestlerDivisionMap() {
      const divisionMap = {};
      this.selectedWrestlers.forEach((w) => {
        divisionMap[w.id] = {
          selectedAgeDivisionId: null,
          selectedWeightClassId: null,
        };
      });
      // odd assignment due to https://v2.vuejs.org/v2/guide/reactivity.html
      this.wrestlerDivisions = { ...this.wrestlerDivisions, ...divisionMap };
    },
    initWrestlerUpsellMap() {
      // Pre-seed an input key for every upsell across the product's plans so the nested
      // object stays reactive in Vue 2 (keys added later via v-model would not be).
      const addOnInputs = {};
      if (this.hasCustomUpsellProduct) {
        _.compact(_.flatMap(this.activeUpsellBillingPlans, 'upsells')).forEach((u) => {
          addOnInputs[u.id] = '';
        });
      }
      const upsellMap = {};
      this.selectedWrestlers.forEach((w) => {
        upsellMap[w.id] = {
          addedToCart: null,
          selectedBillingPlan: null,
          activeSize: null,
          activeColor: null,
          activePriceTierId: null,
          // Add-ons of the upsold product itself (e.g. custom name printing).
          // addOnInputs: upsell id -> user input text. selectedOptionalAddOnIds: opt-in ids.
          addOnInputs: { ...addOnInputs },
          selectedOptionalAddOnIds: [],
        };
      });
      // odd assignment due to https://v2.vuejs.org/v2/guide/reactivity.html
      this.wrestlerUpsells = { ...this.wrestlerUpsells, ...upsellMap };

      // Initialize billing plans for all wrestlers
      this.selectedWrestlers.forEach((w) => {
        this.findAndSetWrestlerUpsellPlan(w.id);
      });
    },
    setWrestlerUpsellColor(wrestlerId, color) {
      this.wrestlerUpsells[wrestlerId].activeColor = color;
      this.findAndSetWrestlerUpsellPlan(wrestlerId);
    },
    setWrestlerUpsellSize(wrestlerId, size) {
      this.wrestlerUpsells[wrestlerId].activeSize = size;
      this.findAndSetWrestlerUpsellPlan(wrestlerId);
    },
    setWrestlerUpsellPriceTier(wrestlerId, priceTierId) {
      this.wrestlerUpsells[wrestlerId].activePriceTierId = priceTierId;
      this.findAndSetWrestlerUpsellPlan(wrestlerId);
    },
    getWrestlerUpsellPrice(wrestlerId) {
      const upsell = this.wrestlerUpsells[wrestlerId];
      if (!upsell || !upsell.selectedBillingPlan) {
        return '';
      }
      const addOnTotal = _.sumBy(
        this.planAddOnsFor(wrestlerId).filter((addOn) => this.addOnIncluded(wrestlerId, addOn)),
        (addOn) => addOn.upsell.amount,
      );
      const total = upsell.selectedBillingPlan.amount + addOnTotal;
      return this.formatAsCurrency(total, 2);
    },
    // Add-ons configured on the upsold product's billing plan (e.g. custom name printing).
    // Returns [{ required, upsell }] for active upsells only.
    planAddOnsFor(wrestlerId) {
      const wrestlerUpsell = this.wrestlerUpsells[wrestlerId];
      const plan = wrestlerUpsell ? wrestlerUpsell.selectedBillingPlan : null;
      if (!plan || !plan.billing_plan_add_ons) {
        return [];
      }
      return _.compact(plan.billing_plan_add_ons.map((addOn) => {
        const upsell = _.find(plan.upsells, { id: addOn.upsell_id });
        if (!upsell || !upsell.active) {
          return null;
        }
        return { required: addOn.required, upsell };
      }));
    },
    addOnIncluded(wrestlerId, addOn) {
      if (addOn.required) {
        return true;
      }
      return _.includes(this.wrestlerUpsells[wrestlerId].selectedOptionalAddOnIds, addOn.upsell.id);
    },
    toggleWrestlerAddOn(wrestlerId, addOn) {
      if (addOn.required) {
        return;
      }
      const ids = this.wrestlerUpsells[wrestlerId].selectedOptionalAddOnIds;
      const idx = ids.indexOf(addOn.upsell.id);
      if (idx === -1) {
        ids.push(addOn.upsell.id);
      } else {
        ids.splice(idx, 1);
      }
    },
    addWrestlerUpsellToCart(wrestlerId) {
      if (!this.wrestlerUpsells[wrestlerId].selectedBillingPlan) {
        return;
      }
      this.wrestlerUpsells[wrestlerId].addedToCart = true;
    },
    popStateHandler(event) {
      if (event.state && 'currentStep' in event.state) {
        this.currentStep = event.state.currentStep;
      }
    },
    initWrestlerCouponMap() {
      const couponMap = {};
      this.selectedWrestlers.forEach((w) => {
        couponMap[w.id] = {
          coupon: { code: null },
          previously_tried_coupon_code: '',
          show_form: false,
          auto_apply_coupon: true,
        };
      });
      // odd assignment due to https://v2.vuejs.org/v2/guide/reactivity.html
      this.wrestlerCoupons = { ...this.wrestlerCoupons, ...couponMap };
    },
    findAndSetWrestlerUpsellPlan(wrestlerId) {
      if (!this.hasCustomUpsellProduct || !this.wrestlerUpsells[wrestlerId]) {
        return;
      }

      const wrestlerUpsell = this.wrestlerUpsells[wrestlerId];

      // Case 1: Only one billing plan total, auto-select it
      if (this.activeUpsellBillingPlans.length === 1) {
        wrestlerUpsell.selectedBillingPlan = _.head(this.activeUpsellBillingPlans);
        return;
      }

      // Case 2: Multiple price tiers (no size/color variants)
      if (this.upsellPriceTiers.length > 1) {
        // Must select a price tier
        if (!wrestlerUpsell.activePriceTierId) {
          // Only set to null if not already null to avoid triggering deep watcher unnecessarily
          if (wrestlerUpsell.selectedBillingPlan !== null) {
            wrestlerUpsell.selectedBillingPlan = null;
          }
          return;
        }

        // Find the billing plan by ID
        const newPlan = _.find(this.activeUpsellBillingPlans, { id: wrestlerUpsell.activePriceTierId });
        if (wrestlerUpsell.selectedBillingPlan !== newPlan) {
          wrestlerUpsell.selectedBillingPlan = newPlan;
        }
        return;
      }

      // Case 3: Products with size/color variants
      // First check to make sure we have all the required information to set this
      const mustSelectColors = this.upsellColors.length > 0 && wrestlerUpsell.activeColor == null;
      const mustSelectSizes = this.upsellSizes.length > 0 && wrestlerUpsell.activeSize == null;
      if (mustSelectSizes || mustSelectColors) {
        // Only set to null if not already null to avoid triggering deep watcher unnecessarily
        if (wrestlerUpsell.selectedBillingPlan !== null) {
          wrestlerUpsell.selectedBillingPlan = null;
        }
        return;
      }

      // Build up the filter to find the right billing plan, depending on whether we have colors or sizes
      const predicate = {};
      if (this.upsellSizes.length > 0) {
        predicate.size = wrestlerUpsell.activeSize;
      }
      if (this.upsellColors.length > 0) {
        predicate.color = wrestlerUpsell.activeColor;
      }

      wrestlerUpsell.selectedBillingPlan = _.head(_.filter(this.activeUpsellBillingPlans, predicate));
    },
    insuranceOptionPicked(event) {
      const state = event.detail.selectedState;
      const { quote } = event.detail;

      if (quote.product.name === 'gap-medical') {
        this.insurance.gapMedical.state = state;
        this.insurance.gapMedical.quote = quote;
        if (state !== 'UNKNOWN') {
          this.$refs.gapMedicalProvider.validate(state);
        }
      } else if (quote.product.name === 'season-interruption' || quote.product.name === 'registration-cancellation') {
        this.insurance.seasonInterruption.state = state;
        this.insurance.seasonInterruption.quote = quote;
        if (state !== 'UNKNOWN') {
          this.$refs.seasonInterruptionProvider.validate(state);
        }
      }

      this.checkForAffixOverflow();
    },
    shortDescription(item) {
      // if item.description contains the words 'Sibling discount' just return sibling discount, nothing else
      if (item.description.includes('Sibling discount')) {
        return 'Sibling discount';
      }

      // Use the item.description, but remove the string 'Registration for'
      return item.description.replace('Registration for ', '');
    },
    handleWrestlerDivisionsUpdate(newVal) {
      this.wrestlerDivisions = newVal;
    },
    getDivisionDisplayName(divisionId) {
      if (!this.session.division_standard || !divisionId) {
        return '';
      }
      const division = this.session.division_standard.age_divisions.find((d) => d.id === divisionId);
      return division ? division.display_name : '';
    },
    getWeightClassDisplayName(divisionId, weightClassId) {
      if (!this.session.division_standard || !divisionId || !weightClassId) {
        return '';
      }
      const division = this.session.division_standard.age_divisions.find((d) => d.id === divisionId);
      if (!division) {
        return '';
      }
      const weightClass = division.weight_classes.find((w) => w.id === weightClassId);
      return weightClass ? weightClass.display_name : '';
    },
    scrollTo(elementId) {
      this.$nextTick(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      });
    },
    inputClasses(error) {
      const normalClasses = 'form-control font-no-ios-zoom';
      if (!error) {
        return normalClasses;
      }

      return `text-danger border-red-500 ${normalClasses}`;
    },
    previousStep() {
      history.back();
    },
    nextStep() {
      if (this.registrationIsClosed) {
        return;
      }

      if (this.currentStep === 'divisions') {
        // Validate all wrestlers have selected divisions
        if (!this.allDivisionSelectionsComplete) {
          return;
        }
        this.currentStep = 'review';
        history.pushState({ currentStep: this.currentStep }, '');
        return;
      }

      if (this.currentStep === 'review') {
        // Update billing profile with saved information
        const params = {
          billing_profile: {
            default_payment_method_reusable: this.reusePaymentMethodInFuture,
            name_on_card: this.nameOnCard,
            address_line_1: this.addressLine1,
            address_line_2: this.addressLine2,
            city: this.city,
            state: this.state,
            zip: this.zip,
          },
        };
        this.savingBillingInfo = true;
        const url = this.$apiService.billingProfileUrl(this.billingProfile.id);
        axios.put(url, params)
          .then((response) => {
            this.savingBillingInfo = false;
            if (this.hasUpsells) {
              this.currentStep = 'upsells';
            } else {
              this.currentStep = 'pay';
            }
            history.pushState({ currentStep: this.currentStep }, '');
          })
          .catch((error) => {
            this.savingBillingInfo = false;
            this.error = true;
            this.errorMessage = `Error saving billing info. ${error.toString()}`;
          });
      } else if (this.currentStep === 'upsells') {
        this.currentStep = 'pay';
        history.pushState({ currentStep: this.currentStep }, '');
      } else if (this.currentStep === 'pay') {
        if (this.cashSelected || this.free) {
          this.createCharge('cash', null);
        } else {
          // If we already have a saved card, use that.
          if (this.hasSavedCard && !this.updatingSavedCard) {
            this.tokenized(this.billingProfile.default_payment_method_id);
          } else {
            this.getPaymentToken();
          }
        }
      }
    },
    tokenized(token) {
      this.registering = true;
      if (this.recurringSession) {
        this.createSubscription(token);
      } else {
        this.createCharge('card', token);
      }
    },
    createCharge: _.throttle(function (payment_method_type, payment_method_id) {
      const vm = this;
      if (vm.creatingCharge) {
        return;
      }

      vm.creatingCharge = true;

      const url = vm.isTeamRegistration
        ? vm.$apiService.checkoutsUrl()
        : vm.$apiService.paymentPlansUrl();
      const params = {
        payment_method_type,
        payment_method_id,
        payment_plan: {
          registrations: vm.buildRegistrationPayload(),
          coupon: vm.couponCode,
          donation_amount: vm.donationAmount || 0,
          insurance_quotes: vm.acceptedInsuranceOffers,
        },
      };

      axios
        .post(url, params)
        .then((response) => {
          vm.$notificationManager.emit(
            'show-toast',
            'Registration completed',
            true,
          );
          vm.paid = true;
          // Trial purchases continue to the class picker, but that routing lives
          // in the funnel (Funnel::TRIAL_SCHEDULING) rather than here — jumping
          // straight to the page would leave the funnel parked on the Pay step
          // and AccessFilter would bounce the family back into checkout.
          if (vm.funnel) {
            vm.goToNextFunnelStep();
          } else if (vm.redirect) {
            vm.registering = false;
            window.location.assign(vm.redirect);
          } else {
            vm.registering = false;
            window.location.assign('/calendar');
          }
        })
        .catch((error) => {
          vm.creatingCharge = false;
          vm.registering = false;
          vm.errorMessage = vm.getErrorMessage(error);
          vm.error = true;
        });
    }, 500),
    buildRegistrationPayload() {
      const registrations = [];
      const vm = this;
      vm.selectedWrestlers.forEach((wp) => {
        const payment_option_id = _.get(vm.selectedPaymentOption, 'id', null);
        const mPayload = {
          payment_option_id,
          registerable: {
            id: wp.id,
            type: 'WrestlerProfile',
          },
        };

        // Add division fields if applicable
        if (vm.hasDivisionSelection && vm.wrestlerDivisions[wp.id]) {
          mPayload.age_division_id = vm.wrestlerDivisions[wp.id].selectedAgeDivisionId;
          mPayload.weight_class_id = vm.wrestlerDivisions[wp.id].selectedWeightClassId;
          mPayload.gender = wp.gender;
        }

        // Add per-wrestler upsell if applicable
        const upsell = vm.wrestlerUpsells[wp.id];
        if (upsell && upsell.addedToCart && upsell.selectedBillingPlan !== null) {
          mPayload.upsold_billing_plan_ids = [upsell.selectedBillingPlan.id];
          // Add-ons of the upsold plan itself (required ones plus opted-in optionals),
          // with per-wrestler user input (e.g. the name to print on a custom singlet)
          const addOns = vm.planAddOnsFor(wp.id)
            .filter((addOn) => vm.addOnIncluded(wp.id, addOn))
            .map((addOn) => ({
              id: addOn.upsell.id,
              user_input: (upsell.addOnInputs[addOn.upsell.id] || '').trim(),
            }));
          if (addOns.length > 0) {
            mPayload.upsold_add_ons = [{
              billing_plan_id: upsell.selectedBillingPlan.id,
              upsells: addOns,
            }];
          }
        }

        // For team registrations, attach per-wrestler coupon
        if (vm.isTeamRegistration && vm.wrestlerCoupons[wp.id] && vm.wrestlerCoupons[wp.id].coupon.code) {
          mPayload.coupon = vm.wrestlerCoupons[wp.id].coupon.code;
        }

        registrations.push(mPayload);
      });
      return registrations;
    },
    // Team registration coupon methods
    applyTeamRegCoupon(wrestlerId) {
      this.wrestlerCoupons[wrestlerId].previously_tried_coupon_code = this.wrestlerCoupons[wrestlerId].coupon.code;
      this.previewCharge(this.previewUrl);
    },
    removeTeamRegCoupon(wrestlerId) {
      this.wrestlerCoupons[wrestlerId].coupon.code = null;
      this.wrestlerCoupons[wrestlerId].previously_tried_coupon_code = '';
      this.previewCharge(this.previewUrl);
    },
    getCheckoutCoupon(checkout) {
      const items = _.flatMap(checkout.manual_invoices, 'invoice_items');
      const couponItem = _.find(items, { transactable_type: 'Coupon' });
      return couponItem ? couponItem.transactable : null;
    },
    getCheckoutWrestlerId(checkout) {
      return _.get(checkout, 'registrations[0].profile.id', null);
    },
    getCheckoutWrestlerName(checkout) {
      return _.get(checkout, 'registrations[0].profile.display_name', '');
    },
    // Subscription methods
    removeCoupon(subRequest) {
      if (subRequest) {
        this.wrestlerCoupons[subRequest.wrestler_profile.id].coupon.code = null;
        // if we explicilty remove, stop showing the previously tried coupon since it was removed and is not invalid
        this.wrestlerCoupons[subRequest.wrestler_profile.id].previously_tried_coupon_code = '';
        // If we explicitly remove a coupon, we need to tell the backend not to apply it automatically
        this.wrestlerCoupons[subRequest.wrestler_profile.id].auto_apply_coupon = false;
        subRequest.coupon = null;
        this.$nextTick(() => {
          this.previewSubscription();
        });
      } else {
        // For removing coupons on other registrations
        this.couponCode = '';
        this.previouslyTriedCouponCode = '';
        this.showCouponForm = true;
        this.$nextTick(() => {
          this.previewCharge(this.previewUrl);
        });
      }
    },
    applyCoupon(subRequest) {
      // If we explicitly add a coupon, we also tell the backend not to apply it other coupons automatically
      this.wrestlerCoupons[subRequest.wrestler_profile.id].auto_apply_coupon = false;
      this.wrestlerCoupons[subRequest.wrestler_profile.id].show_form = false;
      // Cache this so we can make an intelligent ui to the user about whether it was applied
      this.wrestlerCoupons[subRequest.wrestler_profile.id].previously_tried_coupon_code = this.wrestlerCoupons[subRequest.wrestler_profile.id].coupon.code;
      this.previewSubscription();
    },
    checkForDuplicateSubs(billingPlan) {
      this.selectedWrestlers.forEach((wp) => {
        const url = this.$apiService.wrestlerBillingSubscriptions(wp.id);
        axios.get(url)
          .then((response) => {
            const { data } = response;
            const activeSubs = _.filter(data.subscriptions, { status: 'paid' });
            const existingSubBillingPlans = _.flatMap(activeSubs, 'billing_plans');
            const existingProductIds = _.flatMap(existingSubBillingPlans, 'product.id');
            if (_.includes(existingProductIds, billingPlan.product.id)) {
              this.confirmWrestler(wp, billingPlan, existingSubBillingPlans);
            }
          })
          .catch((error) => {
            // TODO: handle possible error
          });
      });
    },
    confirmWrestler(wp, billingPlanSelected, existingBillingPlans) {
      const existingNames = _.join(_.compact(_.map(existingBillingPlans, (plan) => {
        if (plan.is_wiq_fee || plan.is_tax_item) {
          return null;
        }

        return `${plan.product.name} - ${plan.name}`;
      })));

      swal.fire({
        icon: 'warning',
        title: `${wp.first_name} ${wp.last_name} is already signed up for ${existingNames}`,
        text: `Are you sure you want to sign up ${wp.first_name} ${wp.last_name} for ${billingPlanSelected.product.name} ${billingPlanSelected.name}?`,
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: 'Cancel sign up',
        cancelButtonText: `Sign up ${wp.first_name} again`,
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-block',
          cancelButton: 'btn btn-success btn-block',
        },
      })
        .then((result) => {
          if (result.value) {
            // They've elected to cancel this sign up, bail.
            this.exitRegistration();
          }
        });
    },
    toggleShowCouponFor(subRequest) {
      this.wrestlerCoupons[subRequest.wrestler_profile.id].show_form = !this.wrestlerCoupons[subRequest.wrestler_profile.id].show_form;
    },
    feeFor(plan) {
      if (!plan) {
        return null;
      }

      if (!plan.associated_fee_id) {
        return null;
      }

      return _.find(this.session.product.billing_plans, {
        id: plan.associated_fee_id,
        is_wiq_fee: true,
        is_tax_item: false,
        is_one_time_fee: false,
      });
    },
    taxFor(plan) {
      if (!plan) {
        return null;
      }

      if (!plan.associated_tax_id) {
        return null;
      }

      return _.find(this.session.product.billing_plans, {
        id: plan.associated_tax_id,
        is_wiq_fee: false,
        is_tax_item: true,
        is_one_time_fee: false,
      });
    },
    oneTimeFeeFor(plan) {
      if (!plan) {
        return null;
      }

      if (!plan.associated_one_time_fee_id) {
        return null;
      }

      return _.find(this.session.product.billing_plans, {
        id: plan.associated_one_time_fee_id,
        is_wiq_fee: false,
        is_tax_item: false,
        is_one_time_fee: true,
      });
    },
    subscriptionPayload() {
      const vm = this;
      const fee = this.feeFor(this.selectedBillingPlan); // wiq fee
      const tax = this.taxFor(this.selectedBillingPlan);
      const oneTimeFee = this.oneTimeFeeFor(this.selectedBillingPlan); // user created one time fee
      return _.map(this.selectedWrestlers, (w) => {
        const plans = [{
          plan_id: this.selectedBillingPlan.id,
          quantity: 1,
        }];
        if (fee) {
          plans.push({
            plan_id: fee.id,
            quantity: 1,
          });
        }
        if (tax) {
          plans.push({
            plan_id: tax.id,
            quantity: 1,
          });
        }
        if (oneTimeFee) {
          plans.push({
            plan_id: oneTimeFee.id,
            quantity: 1,
          });
        }
        return {
          wrestler_profile_id: w.id,
          billing_plans: plans,
          coupon: vm.wrestlerCoupons[w.id].coupon.code,
          auto_apply_coupon: vm.wrestlerCoupons[w.id].auto_apply_coupon,
        };
      });
    },
    membershipParams(payment_method_id) {
      return {
        membership: {
          client_version: 'web-2.0',
          billable: {
            id: this.guardianId,
            type: this.guardianType,
          },
          subscriptions: this.subscriptionPayload(),
          payment_method_id,
        },
      };
    },
    createSubscription: _.throttle(function (payment_method_id) {
      const vm = this;
      if (vm.creatingSubscription) {
        return;
      }

      vm.creatingSubscription = true;
      axios.post(this.$apiService.membershipsUrl(), this.membershipParams(payment_method_id))
        .then((response) => {
          vm.$notificationManager.emit('show-toast', 'Registration completed', true);
          vm.paid = true;
          // Trial purchases continue to the class picker, but that routing lives
          // in the funnel (Funnel::TRIAL_SCHEDULING) rather than here — jumping
          // straight to the page would leave the funnel parked on the Pay step
          // and AccessFilter would bounce the family back into checkout.
          if (vm.funnel) {
            vm.goToNextFunnelStep();
          } else if (vm.redirect) {
            vm.registering = false;
            window.location.assign(vm.redirect);
          } else {
            vm.registering = false;
            window.location.assign('/calendar');
          }
        })
        .catch((error) => {
          vm.creatingSubscription = false;
          vm.registering = false;
          vm.errorMessage = vm.getErrorMessage(error);
          vm.error = true;
          if (vm.clientUpdateRequired(error)) {
            vm.errorMessage = 'A new version of WIQ is needed to complete your request, please refresh the page and retry. Page will auto refresh in 5 seconds...';
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }
        });
    }, 500),
    previewSubscription() {
      const vm = this;
      vm.loadingPreview = true;
      axios.post(this.$apiService.previewMembershipUrl(), this.membershipParams(''))
        .then((response) => {
          vm.previewRequest = response.data;
          vm.loadingPreview = false;
        })
        .catch((error) => {
          vm.loadingPreview = false;
          vm.errorMessage = vm.parseErrorResponse(error);
          vm.error = true;
          if (vm.clientUpdateRequired(error)) {
            vm.errorMessage = 'A new version of WIQ is needed to complete your request, please refresh the page and retry. Page will auto refresh in 5 seconds...';
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }
        });
    },
  },
};
</script>
